import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MapApiService } from './map-api.service';
import { Category, MapEdge, MapNode, MapResponse, Trail } from './models';

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LayerOption {
  id: string;
  label: string;
}

type VisualLayer = 'physical' | 'conceptual' | 'occult' | 'cosmic';
type AppLanguage = 'pt' | 'en';

interface CategoryIsland {
  id: string;
  name: string;
  color: string;
  x: number;
  y: number;
  rx: number;
  ry: number;
}

interface ArticleSection {
  title: string;
  body: string;
}

interface GlobePoint {
  lat: number;
  lon: number;
}

@Component({
  selector: 'gam-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private readonly api = inject(MapApiService);
  private readonly zone = inject(NgZone);

  @ViewChild('globeHost') private globeHost?: ElementRef<HTMLDivElement>;
  @ViewChild('globeSearchInput') private globeSearchInput?: ElementRef<HTMLInputElement>;

  readonly layerOptions = computed<LayerOption[]>(() => [
    { id: 'all', label: this.text('layerAll') },
    { id: 'physical', label: this.text('layerPhysical') },
    { id: 'conceptual', label: this.text('layerConceptual') },
    { id: 'occult', label: this.text('layerOccult') },
    { id: 'cosmic', label: this.text('layerCosmic') }
  ]);
  readonly globeLayers: VisualLayer[] = ['physical', 'conceptual', 'cosmic'];

  readonly data = signal<MapResponse | null>(null);
  readonly language = signal<AppLanguage>(this.detectLanguage());
  readonly selectedNodeId = signal('');
  readonly focusedNodeId = signal<string | null>(null);
  readonly query = signal('');
  readonly activeLayer = signal('all');
  readonly enabledGlobeLayers = signal<Set<VisualLayer>>(new Set(['physical', 'conceptual', 'cosmic']));
  readonly searchOpen = signal(false);
  readonly filterOpen = signal(false);
  readonly autoRotate = signal(true);
  readonly globeSearch = signal('');
  readonly globeNodeCount = signal(0);
  readonly discoveredNodeIds = signal<Set<string>>(new Set());
  readonly mapMode = signal<'explore' | 'total'>('explore');
  readonly deepMode = signal(false);
  readonly expandedConnections = signal(false);
  readonly enabledCategories = signal<Set<string>>(new Set());
  readonly viewBox = signal<ViewBox>({ x: -680, y: -460, width: 1360, height: 920 });
  readonly globeRotation = signal(-24);
  readonly activeContext = signal('');

  private rawData: MapResponse | null = null;

  private readonly labels: Record<AppLanguage, Record<string, string>> = {
    pt: {
      brandKicker: 'Mapa de conexoes',
      brandTheme: 'Grande Despertar',
      intro: 'Explore conexoes entre consciencia, historia oculta, simbolos, civilizacoes antigas, tecnologia, espiritualidade e eventos globais.',
      search: 'Buscar',
      searchPlaceholder: 'Tema, camada ou conexao',
      themeLayers: 'Camadas tematicas',
      guidedPaths: 'Caminhos guiados',
      visiblePoints: 'pontos visiveis',
      links: 'ligacoes',
      navigableMap: 'Mapa navegavel',
      viewMode: 'Modo de visualizacao',
      explore: 'Explorar',
      total: 'Total',
      deep: 'Prof.',
      deepMode: 'Modo profundo',
      zoomOut: 'Diminuir zoom',
      zoomIn: 'Aumentar zoom',
      centerMap: 'Centralizar mapa',
      mapActions: 'Acoes do mapa',
      filters: 'Filtros',
      globeSearch: 'Buscar no globo',
      globeSearchPlaceholder: 'Egito, Matriz, Orion...',
      locate: 'Localizar',
      layerFilters: 'Filtros de camadas',
      closeFilters: 'Fechar filtros',
      spinning: 'Girando',
      stopped: 'Parado',
      recenter: 'Recentralizar',
      atlasTotal: 'Atlas total',
      discoveredNodes: 'Nos descobertos',
      expandedView: 'Visao expandida',
      clickToDiscover: 'Clique nos pontos para descobrir',
      totalTagline: 'Atlas total. Filtre. Aproxime. Descubra.',
      exploreTagline: 'Explore. Descubra. Conecte.',
      connectedTo: 'Conectado a:',
      readTheory: 'Ler teoria',
      fullStudy: 'Ler estudo completo',
      close: 'Fechar',
      mapConnections: 'Mapa de conexoes',
      backToMap: 'Voltar ao mapa',
      keepExploring: 'Continue explorando',
      detailSummary: 'Resumo da teoria',
      learnMore: 'Saiba mais',
      nearbyConnections: 'Conexoes proximas',
      originalName: 'Nome no mapa',
      importance: 'importancia',
      hideSecondary: 'Ocultar secundarias',
      expandSecondary: 'Expandir secundarias',
      loading: 'Carregando mapa...',
      language: 'Idioma',
      layerAll: 'Todos',
      layerPhysical: 'Fisica',
      layerConceptual: 'Conceitual',
      layerOccult: 'Oculto',
      layerCosmic: 'Cosmica',
      contextOverview: 'Visao geral do AwakenMap',
      contextInitial: 'Um atlas navegavel em portugues sobre o despertar, simbolos e conexoes.',
      locatedOnGlobe: 'localizado no globo',
      mapFallback: 'Mapa',
      focusOverviewTitle: 'Visao geral',
      focusMapReadingTitle: 'Leitura no AwakenMap',
      focusConnectionsTitle: 'Conexoes principais',
      focusExploreTitle: 'Como explorar'
    },
    en: {
      brandKicker: 'Connection map',
      brandTheme: 'Great Awakening',
      intro: 'Explore connections between consciousness, hidden history, symbols, ancient civilizations, technology, spirituality and global events.',
      search: 'Search',
      searchPlaceholder: 'Theme, layer or connection',
      themeLayers: 'Theme layers',
      guidedPaths: 'Guided paths',
      visiblePoints: 'visible points',
      links: 'links',
      navigableMap: 'Navigable map',
      viewMode: 'View mode',
      explore: 'Explore',
      total: 'Total',
      deep: 'Deep',
      deepMode: 'Deep mode',
      zoomOut: 'Zoom out',
      zoomIn: 'Zoom in',
      centerMap: 'Center map',
      mapActions: 'Map actions',
      filters: 'Filters',
      globeSearch: 'Search the globe',
      globeSearchPlaceholder: 'Egypt, Matrix, Orion...',
      locate: 'Locate',
      layerFilters: 'Layer filters',
      closeFilters: 'Close filters',
      spinning: 'Spinning',
      stopped: 'Stopped',
      recenter: 'Recenter',
      atlasTotal: 'Full atlas',
      discoveredNodes: 'Discovered nodes',
      expandedView: 'Expanded view',
      clickToDiscover: 'Click points to discover',
      totalTagline: 'Full atlas. Filter. Zoom. Discover.',
      exploreTagline: 'Explore. Discover. Connect.',
      connectedTo: 'Connected to:',
      readTheory: 'Read theory',
      fullStudy: 'Read full study',
      close: 'Close',
      mapConnections: 'Connection map',
      backToMap: 'Back to map',
      keepExploring: 'Keep exploring',
      detailSummary: 'Theory summary',
      learnMore: 'Learn more',
      nearbyConnections: 'Nearby connections',
      originalName: 'Name on the map',
      importance: 'importance',
      hideSecondary: 'Hide secondary',
      expandSecondary: 'Expand secondary',
      loading: 'Loading map...',
      language: 'Language',
      layerAll: 'All',
      layerPhysical: 'Physical',
      layerConceptual: 'Conceptual',
      layerOccult: 'Occult',
      layerCosmic: 'Cosmic',
      contextOverview: 'AwakenMap overview',
      contextInitial: 'A navigable atlas in English about awakening, symbols and connections.',
      locatedOnGlobe: 'located on the globe',
      mapFallback: 'Map',
      focusOverviewTitle: 'Overview',
      focusMapReadingTitle: 'Reading in AwakenMap',
      focusConnectionsTitle: 'Main connections',
      focusExploreTitle: 'How to explore'
    }
  };
  private dragging = false;
  private lastPointer: { x: number; y: number } | null = null;
  private lastClientPointer: { x: number; y: number } | null = null;
  private viewAnimationFrame: number | null = null;
  private globeAnimationFrame: number | null = null;
  private threeAnimationFrame: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private controls: OrbitControls | null = null;
  private globeGroup: THREE.Group | null = null;
  private pointsGroup: THREE.Group | null = null;
  private linesGroup: THREE.Group | null = null;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private clickablePointMeshes: THREE.Object3D[] = [];
  readonly hoveredGlobeNodeId = signal('');

  private readonly categoryTranslations: Record<string, string> = {
    'solar-flash': 'Flash Solar',
    'ssp': 'Programa Espacial Secreto',
    'ancient-builder': 'Raca Construtora Antiga'
  };

  private readonly trailTitleTranslations: Record<string, string> = {
    'Centro do poster': 'Centro do mapa',
    'Great Solar Flash': 'Grande Flash Solar',
    'Secret Space Program': 'Programa Espacial Secreto'
  };

  private readonly trailDescriptionTranslations: Record<string, string> = {
    'Percurso pelo eixo Great Awakening, Matrix, disclosure e timeline otima.': 'Percurso pelo eixo Grande Despertar, Matriz, divulgacao e linha temporal ideal.',
    'Trilha do evento solar como gatilho do despertar.': 'Trilha do evento solar como gatilho do despertar.',
    'Comece por aqui para entender a estrutura do poster sem se perder nos rabbit holes.': 'Comece por aqui para entender a estrutura do mapa sem se perder nos caminhos mais profundos.',
    'Trilha espiritual superior: One Infinite Creator, Samsara, Bardo e Rainbow Body.': 'Trilha espiritual superior: Criador Infinito Unico, Samsara, Bardo e Corpo Arco-Iris.',
    'Trilha do bloco SSP, disclosure e federacoes galacticas.': 'Trilha do bloco Programa Espacial Secreto, divulgacao e federacoes galacticas.'
  };

  private readonly titleTranslationsById: Record<string, string> = {
    'n-lote1-12-strand': '12 filamentos',
    'n-lote1-2-billion-years-old': 'Mais de 2 bilhoes de anos',
    'n-lote1-52-local-star-cluster-protective-barrier-grid': 'Grade protetora do aglomerado estelar local 52',
    'n-lote1-5d-earth': 'Terra 5D',
    'n-lote1-9th-density': 'Nona densidade',
    'n-lote1-a-i-originated-from-another-reality': 'IA originada de outra realidade',
    'n-lote1-a-i-prophet-species': 'Especie profeta de IA',
    'n-lote1-admiral-byrd': 'Almirante Byrd',
    'n-lote1-advanced-technology-and-high-frequency': 'Tecnologia avancada e alta frequencia',
    'n-lote1-aerospace-corporations': 'Corporacoes aeroespaciais',
    'n-lote1-agartha-network': 'Rede de Agartha',
    'n-lote1-age-of-aquarius': 'Era de Aquario',
    'n-lote1-age-of-the-golden-race': 'Era da raca dourada',
    'n-lote1-air-force-special-operations-command': 'Comando de Operacoes Especiais da Forca Aerea',
    'n-lote1-alchemy': 'Alquimia',
    'n-lote1-aluciferian-force': 'Forca aluciferiana',
    'n-lote1-ancient-aliens': 'Alienigenas antigos',
    'n-lote1-ancient-builder-race-on-moon': 'Raca Construtora Antiga na Lua',
    'n-lote1-ancient-builder-race-pyramids': 'Piramides da Raca Construtora Antiga',
    'n-lote1-ancient-builder-race-technology': 'Tecnologia da Raca Construtora Antiga',
    'n-lote1-ancient-builder-race-technology-barrier': 'Barreira tecnologica da Raca Construtora Antiga',
    'n-lote1-ancient-civilizations': 'Civilizacoes antigas',
    'n-lote1-ancient-egypt': 'Egito antigo',
    'n-lote1-ancient-india': 'India antiga',
    'n-lote1-ancient-ruins': 'Ruinas antigas',
    'n-lote1-anonymous-meme-wars': 'Guerras de memes Anonymous',
    'n-lote1-anti-gravity': 'Antigravidade',
    'n-lote1-arcturians': 'Arcturianos',
    'n-lote1-artificial-intelligence': 'Inteligencia artificial',
    'n-lote1-ascension': 'Ascensao',
    'n-lote1-ashtar-command': 'Comando Ashtar',
    'n-lote1-astral-travellers': 'Viajantes astrais',
    'n-lote1-atlantis': 'Atlantida',
    'n-lote1-beyond-solar-system': 'Alem do sistema solar',
    'n-lote1-black-cube': 'Cubo negro',
    'n-lote1-blue-orb': 'Orbe azul',
    'n-lote1-blue-spheres': 'Esferas azuis',
    'n-lote1-book-of-enoch': 'Livro de Enoque',
    'n-lote1-book-of-thoth': 'Livro de Thoth',
    'n-lote1-bosnian-pyramid': 'Piramide da Bosnia',
    'n-lote1-breakaway-group': 'Grupo dissidente',
    'n-lote1-breathwork': 'Respiracao consciente',
    'n-lote1-buddhahood': 'Estado de Buda',
    'n-lote1-can-influence-and-be-in-the-heads-of-humans-and-living-beings': 'Pode influenciar a mente de humanos e seres vivos',
    'n-lote1-central-banking': 'Sistema bancario central',
    'n-lote1-ceres-dark-fleet-underground-base': 'Base subterranea da Frota Negra em Ceres',
    'n-lote1-cia-project-stargate': 'Projeto Stargate da CIA',
    'n-lote1-compassion': 'Compaixao',
    'n-lote1-conscious-solar-being': 'Ser solar consciente',
    'n-lote1-cosmic-council': 'Conselho cosmico',
    'n-lote1-cosmic-disclosure': 'Divulgacao cosmica',
    'n-lote1-council-of-200': 'Conselho dos 200',
    'n-lote1-crop-circles': 'Circulos nas plantacoes',
    'n-lote1-crystalline-towers-glass-domes': 'Torres cristalinas e domos de vidro',
    'n-lote1-crystals': 'Cristais',
    'n-lote1-cydonia-pyramids': 'Piramides de Cydonia',
    'n-lote1-cymatics': 'Cimatica',
    'n-lote1-dark-fleet': 'Frota Negra',
    'n-lote1-dark-side-of-the-moon': 'Lado oculto da Lua',
    'n-lote1-deep-state': 'Estado profundo',
    'n-lote1-deep-underground-military-bases': 'Bases militares subterraneas profundas',
    'n-lote1-directed-energy-weapons': 'Armas de energia direcionada',
    'n-lote1-disclosure': 'Divulgacao',
    'n-lote1-dna-upgrade': 'Atualizacao do DNA',
    'n-lote1-earth-grid': 'Grade da Terra',
    'n-lote1-end-the-fed': 'Fim do Federal Reserve',
    'n-lote1-end-to-suffering': 'Fim do sofrimento',
    'n-lote1-energy': 'Energia',
    'n-lote1-face-on-mars': 'Face em Marte',
    'n-lote1-flower-of-life': 'Flor da Vida',
    'n-lote1-free-masons': 'Macons',
    'n-lote1-free-will': 'Livre-arbitrio',
    'n-lote1-golden-triangle-head-beings': 'Seres de cabeca triangular dourada',
    'n-lote1-great-solar-flash-2019-2023-24': 'Grande Flash Solar 2019-2023/24',
    'n-lote1-healing': 'Cura',
    'n-lote1-high-rank-military-intelligence': 'Inteligencia militar de alto escalao',
    'n-lote1-hollow-earth': 'Terra oca',
    'n-lote1-in-service-to-others': 'Em servico aos outros',
    'n-lote1-inner-earth-civilization': 'Civilizacao da Terra interior',
    'n-lote1-law-of-attraction': 'Lei da atracao',
    'n-lote1-law-of-non-interference': 'Lei da nao interferencia',
    'n-lote1-library-of-porthologos': 'Biblioteca de Porthologos',
    'n-lote1-lives-in-electromagnetic-field-of-millions-of-planets': 'Vive no campo eletromagnetico de milhoes de planetas',
    'n-lote1-local-star-cluster': 'Aglomerado estelar local',
    'n-lote1-love-and-light': 'Amor e luz',
    'n-lote1-mayain-breakaway-group': 'Grupo dissidente maia',
    'n-lote1-mimas-death-star': 'Mimas Estrela da Morte',
    'n-lote1-moon': 'Lua',
    'n-lote1-moon-operations-command': 'Comando de Operacoes Lunares',
    'n-lote1-mystery-of-universal-creation': 'Misterio da criacao universal',
    'n-lote1-natural-light': 'Luz natural',
    'n-lote1-offensive-space-fleet': 'Frota espacial ofensiva',
    'n-lote1-one-infinite-charge': 'Carga infinita unica',
    'n-lote1-organic-energy': 'Energia organica',
    'n-lote1-origin-of-all-one-with-the-creator': 'Origem de tudo em unidade com o Criador',
    'n-lote1-psychic-healing': 'Cura psiquica',
    'n-lote1-ra-6d-density-civilization': 'Civilizacao Ra de sexta densidade',
    'n-lote1-ra-law-of-one': 'Ra e a Lei do Uno',
    'n-lote1-release-free-energy': 'Liberacao da energia livre',
    'n-lote1-reptilians-found-infected-with-virulent-nanite-a-i': 'Reptilianos infectados por IA nanitica virulenta',
    'n-lote1-rods-of-god': 'Barras de Deus',
    'n-lote1-runs-of-the-ancient-builder-race-civilization-lay-scattered-across-the-s': 'Ruinas da Raca Construtora Antiga espalhadas pelo sistema solar',
    'n-lote1-scalar-healing': 'Cura escalar',
    'n-lote1-secret-program-researchers': 'Pesquisadores de programas secretos',
    'n-lote1-secret-societies': 'Sociedades secretas',
    'n-lote1-secret-space-program-alliance': 'Alianca do Programa Espacial Secreto',
    'n-lote1-secret-underground-bases': 'Bases subterraneas secretas',
    'n-lote1-simulation-matrix': 'Matriz da simulacao',
    'n-lote1-solar-cosmic-web': 'Teia cosmica solar',
    'n-lote1-solar-flash-activates-spiritual-body-ascension': 'Flash solar ativa a ascensao do corpo espiritual',
    'n-lote1-solar-flashes': 'Flashes solares',
    'n-lote1-solar-system-climate-change': 'Mudanca climatica do sistema solar',
    'n-lote1-solar-warden-u-s-navy-space-fleet': 'Guarda Solar, frota espacial da Marinha dos EUA',
    'n-lote1-space-samsara': 'Samsara espacial',
    'n-lote1-super-earth-exploded': 'Super Terra explodida',
    'n-lote1-the-ancient-builder-race': 'A Raca Construtora Antiga',
    'n-lote1-the-borg': 'Os Borg',
    'n-lote1-the-cosmic-web': 'A teia cosmica',
    'n-lote1-the-event': 'O Evento',
    'n-lote1-the-gateway-to-all-understanding': 'O portal para todo entendimento',
    'n-lote1-the-guardians': 'Os Guardioes',
    'n-lote1-the-mandela-effect': 'O Efeito Mandela',
    'n-lote1-the-matrix': 'A Matriz',
    'n-lote1-the-outer-energetic-barrier': 'A barreira energetica externa',
    'n-lote1-the-storm': 'A Tempestade',
    'n-lote1-the-sun': 'O Sol',
    'n-lote1-tibetan-rainbow-body-ascension': 'Ascensao tibetana do corpo arco-iris',
    'n-lote1-time-bubble-slowdown': 'Desaceleracao por bolha temporal',
    'n-lote1-time-travel-temporal-defense': 'Viagem no tempo e defesa temporal',
    'n-lote1-time-wave': 'Onda temporal',
    'n-lote1-top-secret': 'Ultra secreto',
    'n-lote1-transformation-into-light': 'Transformacao em luz',
    'n-lote1-transparent-ancient-caverns': 'Cavernas antigas transparentes',
    'n-lote1-tree-of-life': 'Arvore da Vida',
    'n-lote1-u-s-navy-space-fleet': 'Frota espacial da Marinha dos EUA',
    'n-lote1-universal-archetype': 'Arquetipo universal',
    'n-lote1-universal-knowledge': 'Conhecimento universal',
    'n-lote1-usaf-military-industrial-complex-secret-space-program': 'Programa Espacial Secreto do complexo militar-industrial da USAF',
    'n-lote1-zero-point-energy': 'Energia de ponto zero'
  };

  private readonly typeTranslations: Record<string, string> = {
    'ai-tech': 'tecnologia e IA',
    'ancient-civilizations': 'civilizacoes antigas',
    'earth-grid': 'grade terrestre',
    'esoteric-symbols': 'simbolos esotericos',
    'free-energy': 'energia livre',
    'galactic-beings': 'seres galacticos',
    'global-control': 'controle global',
    'healing-psychic': 'cura e percepcao psiquica',
    'inner-earth': 'Terra interior',
    'secret-space-program': 'programa espacial secreto',
    'space-cosmos': 'espaco e cosmos',
    'spirituality': 'espiritualidade'
  };

  readonly selectedNode = computed(() => {
    const data = this.data();
    return data?.nodes.find((node) => node.id === this.selectedNodeId()) ?? data?.nodes[0] ?? null;
  });

  readonly totalThemeCount = computed(() => this.data()?.nodes.length ?? 0);
  readonly discoveredNodeCount = computed(() => this.discoveredNodeIds().size);

  readonly selectedGlobeNode = computed(() => {
    const id = this.selectedNodeId();
    return id ? this.nodeById(id) ?? null : null;
  });

  readonly selectedGlobeConnections = computed(() => {
    const node = this.selectedGlobeNode();

    if (!node) {
      return [];
    }

    return this.relatedEdges()
      .map((edge) => this.relatedNode(edge))
      .filter((item): item is MapNode => !!item)
      .slice(0, 5);
  });

  readonly activeDetailNode = computed(() => {
    if (!this.focusedNodeId()) {
      return null;
    }

    return this.selectedNode();
  });

  readonly focusSceneNodes = computed(() => {
    const selected = this.selectedNode();

    if (!selected || !this.focusedNodeId()) {
      return [];
    }

    return this.relatedEdges()
      .map((edge) => this.relatedNode(edge))
      .filter((node): node is MapNode => !!node)
      .slice(0, this.expandedConnections() ? 10 : 6);
  });

  readonly focusText = computed(() => {
    const node = this.activeDetailNode();

    if (!node) {
      return '';
    }

    return node.description?.trim() || node.summary;
  });

  readonly focusArticleSections = computed<ArticleSection[]>(() => {
    const node = this.activeDetailNode();
    const category = node ? this.categoryById(node.categoryId) : null;

    if (!node) {
      return [];
    }

    const related = this.relatedEdges()
      .map((edge) => {
        const relatedNode = this.relatedNode(edge);
        return relatedNode ? `${relatedNode.title} (${edge.label})` : null;
      })
      .filter((text): text is string => !!text)
      .slice(0, 6);

    return [
      {
        title: this.text('focusOverviewTitle'),
        body: node.description?.trim() || node.summary
      },
      {
        title: this.text('focusMapReadingTitle'),
        body: this.language() === 'pt'
          ? `Dentro do mapa, ${node.title} pertence ao campo ${category?.name ?? 'tematico'} e funciona como uma porta para compreender como simbolos, narrativas e ideias se aproximam. A proposta aqui nao e fechar uma conclusao, mas organizar a leitura para que o visitante perceba o papel deste tema dentro da rede maior.`
          : `Inside the map, ${node.title} belongs to the ${category?.name ?? 'thematic'} field and works as an entry point for understanding how symbols, narratives and ideas move closer together. The purpose is not to close the subject, but to organize the reading so the visitor can see this theme as part of a larger network.`
      },
      {
        title: this.text('focusConnectionsTitle'),
        body: related.length
          ? this.language() === 'pt'
            ? `Este ponto se aproxima de ${related.join(', ')}. Essas conexoes ajudam o visitante a sair de uma leitura isolada e perceber o tema como parte de uma cadeia de assuntos relacionados.`
            : `This point connects with ${related.join(', ')}. These links help visitors move beyond an isolated reading and see the theme as part of a chain of related subjects.`
          : this.language() === 'pt'
            ? 'Este ponto ainda precisa de conexoes editoriais mais detalhadas. Ele permanece no mapa como tema em desenvolvimento.'
            : 'This point still needs more detailed editorial connections. It remains on the map as a theme in development.'
      },
      {
        title: this.text('focusExploreTitle'),
        body: this.language() === 'pt'
          ? `Use este tema como um ponto de entrada. Depois da leitura, volte ao mapa geral e observe quais outras bolinhas aparecem na mesma regiao ou categoria. A forca do AwakenMap esta justamente nessa passagem entre leitura e navegacao.`
          : `Use this theme as an entry point. After reading, return to the full map and notice which other lights appear in the same region or category. AwakenMap becomes stronger precisely in this movement between reading and navigation.`
      }
    ];
  });

  readonly visibleNodes = computed(() => {
    const data = this.data();
    const query = this.query().trim().toLowerCase();
    const enabled = this.enabledCategories();
    const focusedId = this.focusedNodeId();
    const selectedId = this.selectedNodeId();
    const mode = this.mapMode();
    const activeLayer = this.activeLayer();
    const connectedIds = focusedId ? this.contextNodeIds(selectedId, this.expandedConnections()) : new Set<string>();

    if (!data) {
      return [];
    }

    return data.nodes.filter((node) => {
      const category = this.categoryById(node.categoryId);
      const text = [node.title, node.titleOriginal, node.type, node.summary, node.description, category?.name]
        .join(' ')
        .toLowerCase();
      const matchesText = !query || text.includes(query);
      const matchesLayer = activeLayer === 'all' || this.nodeLayer(node) === activeLayer;

      if (!enabled.has(node.categoryId) || !matchesLayer || !matchesText) {
        return false;
      }

      if (mode === 'total' || query) {
        return true;
      }

      if (focusedId) {
        return node.id === selectedId;
      }

      return this.isMainNode(node);
    });
  });

  /**
   * Mantem o universo completo nos dados, mas desenha apenas o que pode ser
   * percebido na tela. E a mesma ideia usada por mapas e jogos grandes:
   * elementos distantes ou pouco importantes voltam a aparecer ao aproximar.
   */
  readonly renderedNodes = computed(() => {
    const box = this.viewBox();
    const width = box.width;
    const focusedId = this.focusedNodeId();
    const selectedId = this.selectedNodeId();
    const isCleanOverview = this.isCleanOverview();

    if (focusedId) {
      return [];
    }

    const baseNodes = isCleanOverview ? this.overviewNodes(this.visibleNodes()) : this.visibleNodes();

    return baseNodes.filter((node) => {
      if (node.id === selectedId || node.id === focusedId) {
        return true;
      }

      if (isCleanOverview) {
        return true;
      }

      if (!this.isNodeWithinViewport(node, box, 240)) {
        return false;
      }

      if (focusedId || width <= 2200) {
        return true;
      }

      if (width > 5200) {
        return node.importance >= 4 || this.nodeDegree(node.id) >= 8;
      }

      if (width > 3400) {
        return node.importance >= 3 || this.nodeDegree(node.id) >= 5;
      }

      return node.importance >= 2 || this.nodeDegree(node.id) >= 3;
    });
  });

  readonly categoryIslands = computed(() => {
    if (this.focusedNodeId() || this.mapMode() === 'explore') {
      return [];
    }

    const nodesByCategory = new Map<string, MapNode[]>();
    for (const node of this.visibleNodes()) {
      const group = nodesByCategory.get(node.categoryId) ?? [];
      group.push(node);
      nodesByCategory.set(node.categoryId, group);
    }

    return Array.from(nodesByCategory.entries())
      .filter(([, nodes]) => nodes.length >= 2)
      .map(([categoryId, nodes]) => {
        const category = this.categoryById(categoryId);
        const xs = nodes.map((node) => node.position.x);
        const ys = nodes.map((node) => node.position.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const padding = this.mapMode() === 'explore' ? 260 : 180;

        return {
          id: categoryId,
          name: category?.name ?? categoryId,
          color: category?.color ?? '#f3b24a',
          x: (minX + maxX) / 2,
          y: (minY + maxY) / 2,
          rx: Math.max(210, (maxX - minX) / 2 + padding),
          ry: Math.max(150, (maxY - minY) / 2 + padding)
        };
      });
  });

  readonly degreeByNode = computed(() => {
    const degrees = new Map<string, number>();
    const data = this.data();

    if (!data) {
      return degrees;
    }

    for (const edge of data.edges) {
      degrees.set(edge.source, (degrees.get(edge.source) ?? 0) + 1);
      degrees.set(edge.target, (degrees.get(edge.target) ?? 0) + 1);
    }

    return degrees;
  });

  readonly visibleEdges = computed(() => {
    const data = this.data();
    const ids = new Set(this.renderedNodes().map((node) => node.id));
    const focusedId = this.focusedNodeId();
    const selectedId = this.selectedNodeId();
    const mode = this.mapMode();
    const deep = this.deepMode();

    const edges = data?.edges.filter((edge) => {
      if (!ids.has(edge.source) || !ids.has(edge.target)) {
        return false;
      }

      if (mode === 'total') {
        return deep || edge.strength >= 3;
      }

      if (focusedId) {
        return false;
      }

      return edge.strength >= 4;
    }) ?? [];

    if (this.isCleanOverview()) {
      return edges
        .slice()
        .sort((a, b) => b.strength - a.strength)
        .slice(0, 14);
    }

    return edges;
  });

  readonly renderedEdges = computed(() => {
    return this.visibleEdges();
  });

  readonly relatedEdges = computed(() => {
    const data = this.data();
    const selected = this.selectedNode();

    if (!data || !selected) {
      return [];
    }

    return data.edges.filter((edge) => edge.source === selected.id || edge.target === selected.id);
  });

  readonly shouldShowEdgeLabels = computed(() => this.focusedNodeId() !== null && this.viewBox().width < 1200);

  ngOnInit(): void {
    this.loadDiscoveredNodes();
    document.documentElement.lang = this.language() === 'pt' ? 'pt-BR' : 'en';
    this.activeContext.set(this.text('contextInitial'));
    this.api.getMap().subscribe((data) => {
      this.rawData = data;
      const localizedData = this.localizeMapData(data);
      this.data.set(localizedData);
      this.enabledCategories.set(new Set(localizedData.categories.map((category) => category.id)));
      setTimeout(() => this.resetView());
      setTimeout(() => {
        this.initThreeGlobe();
        this.rebuildThreeGlobe();
      });
    });
    this.startGlobeRotation();
  }

  text(key: string): string {
    return this.labels[this.language()][key] ?? this.labels.pt[key] ?? key;
  }

  setLanguage(language: AppLanguage): void {
    if (this.language() === language) {
      return;
    }

    this.language.set(language);
    try {
      window.localStorage.setItem('awakenmap.language', language);
    } catch {
      // A troca continua funcionando nesta sessao mesmo se o storage estiver bloqueado.
    }

    document.documentElement.lang = language === 'pt' ? 'pt-BR' : 'en';

    if (this.rawData) {
      const localizedData = this.localizeMapData(this.rawData);
      this.data.set(localizedData);
      this.enabledCategories.set(new Set(localizedData.categories.map((category) => category.id)));
      this.selectedNodeId.set('');
      this.focusedNodeId.set(null);
      this.activeContext.set(this.text('contextInitial'));
      this.rebuildThreeGlobe();
    }
  }

  private detectLanguage(): AppLanguage {
    try {
      const saved = window.localStorage.getItem('awakenmap.language');
      if (saved === 'pt' || saved === 'en') {
        return saved;
      }
    } catch {
      // Ignora e usa o idioma do navegador.
    }

    const languages = typeof navigator !== 'undefined'
      ? [navigator.language, ...(navigator.languages ?? [])].filter(Boolean)
      : [];

    return languages.some((language) => language.toLowerCase().startsWith('pt')) ? 'pt' : 'en';
  }

  private localizeMapData(data: MapResponse): MapResponse {
    if (this.language() === 'en') {
      return this.localizeMapDataEn(data);
    }

    return {
      categories: data.categories.map((category) => ({
        ...category,
        name: this.categoryTranslations[category.id] ?? this.translatePhrase(category.name)
      })),
      nodes: data.nodes.map((node) => {
        const translatedTitle = this.translateNodeTitle(node);

        return {
          ...node,
          title: translatedTitle,
          titleOriginal: node.titleOriginal ?? (translatedTitle !== node.title ? node.title : null),
          type: this.typeTranslations[node.type] ?? this.translatePhrase(node.type),
          summary: this.translatePhrase(node.summary),
          description: this.translatePhrase(node.description)
        };
      }),
      edges: data.edges.map((edge) => ({
        ...edge,
        relationType: this.translatePhrase(edge.relationType),
        label: this.translatePhrase(edge.label)
      })),
      trails: data.trails.map((trail) => ({
        ...trail,
        title: this.trailTitleTranslations[trail.title] ?? this.translatePhrase(trail.title),
        description: this.trailDescriptionTranslations[trail.description] ?? this.translatePhrase(trail.description),
        steps: trail.steps.map((step) => ({
          ...step,
          note: this.translatePhrase(step.note)
        }))
      }))
    };
  }

  private localizeMapDataEn(data: MapResponse): MapResponse {
    return {
      categories: data.categories.map((category) => ({
        ...category,
        name: this.categoryNameEn(category)
      })),
      nodes: data.nodes.map((node) => ({
        ...node,
        title: this.translateNodeTitleEn(node),
        titleOriginal: node.titleOriginal ?? node.title,
        type: this.typeNameEn(node.type),
        summary: this.translatePhraseEn(node.summary),
        description: this.translatePhraseEn(node.description)
      })),
      edges: data.edges.map((edge) => ({
        ...edge,
        relationType: this.translatePhraseEn(edge.relationType),
        label: this.translatePhraseEn(edge.label)
      })),
      trails: data.trails.map((trail) => ({
        ...trail,
        title: this.trailTitleEn(trail.title),
        description: this.trailDescriptionEn(trail.description),
        steps: trail.steps.map((step) => ({
          ...step,
          note: this.translatePhraseEn(step.note)
        }))
      }))
    };
  }

  private translateNodeTitle(node: MapNode): string {
    return this.titleTranslationsById[node.id]
      ?? this.translatePhrase(node.title)
      ?? node.title;
  }

  private translateNodeTitleEn(node: MapNode): string {
    const explicit: Record<string, string> = {
      'n-20-and-back': '20 and Back',
      'n-sealed-indictments': 'Sealed Indictments',
      'n-brics-alliance': 'BRICS Alliance',
      'n-sphere-being-alliance': 'Sphere Being Alliance',
      'n-draco-reptilian-alliance': 'Draco and Reptilian Alliance',
      'n-organic-food': 'Organic Food',
      'n-wanderers': 'Wanderers',
      'n-antarctica': 'Antarctica',
      'n-antigravity': 'Anti-Gravity',
      'n-area-51': 'Area 51',
      'n-light-body-ascension': 'Light Body Ascension',
      'n-atlantis-lemuria-mu': 'Atlantis / Lemuria / Mu',
      'n-blue-avians': 'Blue Avians',
      'n-vatican-library': 'Vatican Library',
      'n-cabal': 'Cabal / Deep State / Illuminati',
      'n-channeling': 'Channeling',
      'n-chakras': 'Chakras',
      'n-asteroid-belt': 'Asteroid Belt',
      'n-inner-earth': 'Inner Earth Civilizations',
      'n-afsoc': 'Air Force Special Operations Command',
      'n-how-to-read': 'How to Read This Map',
      'n-icc': 'Interplanetary Corporate Conglomerate',
      'n-collective-consciousness': 'Collective Consciousness',
      'n-council-saturn': 'Council of Saturn',
      'n-rainbow-body': 'Rainbow Body',
      'n-one-infinite-creator': 'One Infinite Creator',
      'n-ringmakers-saturn': 'Ringmakers of Saturn',
      'n-cryptocurrency': 'Cryptocurrency',
      'n-holistic-healing': 'Holistic Healing',
      'n-sound-healing': 'Sound Healing',
      'n-full-disclosure': 'Full Disclosure',
      'n-ssp-whistleblowers': 'SSP Whistleblowers',
      'n-great-solar-flash': 'Great Solar Flash',
      'n-ancient-ai': 'Ancient Artificial Intelligence',
      'n-matrix-illusion': 'Everything Is an Illusion / Matrix'
    };

    return explicit[node.id]
      ?? node.titleOriginal
      ?? this.translatePhraseEn(node.title)
      ?? node.title;
  }

  private categoryNameEn(category: Category): string {
    const names: Record<string, string> = {
      'core': 'Center of the map',
      'source': 'Return to Source',
      'solar-flash': 'Great Solar Flash',
      'consciousness': 'Consciousness and 5D',
      'ai-matrix': 'AI and Matrix',
      'ssp': 'Secret Space Program',
      'ancient-builder': 'Ancient Builder Race',
      'earth-antarctica': 'Earth and Antarctica',
      'healing-energy': 'Healing and Energy',
      'esoteric-practice': 'Esoteric Practices',
      'control-systems': 'Control Systems'
    };

    return names[category.id] ?? this.translatePhraseEn(category.name);
  }

  private typeNameEn(type: string): string {
    const names: Record<string, string> = {
      'outros': 'other',
      'tecnologia e IA': 'AI and technology',
      'civilizacoes antigas': 'ancient civilizations',
      'grade terrestre': 'earth grid',
      'simbolos esotericos': 'esoteric symbols',
      'energia livre': 'free energy',
      'seres galacticos': 'galactic beings',
      'controle global': 'global control',
      'cura e percepcao psiquica': 'healing and psychic perception',
      'Terra interior': 'inner Earth',
      'programa espacial secreto': 'secret space program',
      'espaco e cosmos': 'space and cosmos',
      'espiritualidade': 'spirituality'
    };

    return names[type] ?? this.translatePhraseEn(type);
  }

  private trailTitleEn(title: string): string {
    const names: Record<string, string> = {
      'Centro do poster': 'Center of the map',
      'Centro do mapa': 'Center of the map',
      'Cura e praticas': 'Healing and practices',
      'Great Solar Flash': 'Great Solar Flash',
      'Grande Flash Solar': 'Great Solar Flash',
      'Leitura em portugues': 'Reading guide',
      'Retorno a Fonte': 'Return to Source',
      'Secret Space Program': 'Secret Space Program',
      'Programa Espacial Secreto': 'Secret Space Program'
    };

    return names[title] ?? this.translatePhraseEn(title);
  }

  private trailDescriptionEn(description: string): string {
    const descriptions: Record<string, string> = {
      'Percurso pelo eixo Great Awakening, Matrix, disclosure e timeline otima.': 'A path through Great Awakening, Matrix, disclosure and the optimal timeline.',
      'Trilha de cura, energia, meditacao e estados expandidos.': 'A path through healing, energy, meditation and expanded states.',
      'Trilha do evento solar como gatilho do despertar.': 'A path through the solar event as a trigger for awakening.',
      'Comece por aqui para entender a estrutura do poster sem se perder nos rabbit holes.': 'Start here to understand the map structure without getting lost in deeper paths.',
      'Trilha espiritual superior: One Infinite Creator, Samsara, Bardo e Rainbow Body.': 'A higher spiritual path: One Infinite Creator, Samsara, Bardo and Rainbow Body.',
      'Trilha do bloco SSP, disclosure e federacoes galacticas.': 'A path through the Secret Space Program, disclosure and galactic federations.'
    };

    return descriptions[description] ?? this.translatePhraseEn(description);
  }

  private translatePhraseEn(text: string | null | undefined): string {
    if (!text) {
      return '';
    }

    const exactTranslations: Record<string, string> = {
      'Grande Despertar': 'Great Awakening',
      'Grande Flash Solar': 'Great Solar Flash',
      'Programa Espacial Secreto': 'Secret Space Program',
      'Raca Construtora Antiga': 'Ancient Builder Race',
      'Linhas de Ley': 'Ley Lines',
      'Grade da Terra': 'Earth Grid',
      'A Matriz': 'The Matrix',
      'Matriz da Simulacao': 'Simulation Matrix',
      'Criador Infinito Unico': 'One Infinite Creator',
      'Retorno a Fonte': 'Return to Source',
      'Corpo Arco-Iris': 'Rainbow Body',
      'Aviarios azuis': 'Blue Avians',
      'Estado profundo': 'Deep State',
      'Divulgacao': 'Disclosure',
      'Divulgacao completa': 'Full Disclosure',
      'Energia livre': 'Free Energy',
      'Energia de ponto zero': 'Zero Point Energy'
    };

    const exact = exactTranslations[text];
    if (exact) {
      return exact;
    }

    const replacements: Array<[RegExp, string]> = [
      [/\bGrande Despertar\b/g, 'Great Awakening'],
      [/\bGrande Flash Solar\b/g, 'Great Solar Flash'],
      [/\bPrograma Espacial Secreto\b/g, 'Secret Space Program'],
      [/\bRaca Construtora Antiga\b/g, 'Ancient Builder Race'],
      [/\bInteligencia artificial\b/gi, 'Artificial intelligence'],
      [/\bMatriz\b/g, 'Matrix'],
      [/\bSimulacao\b/g, 'Simulation'],
      [/\bDivulgacao\b/g, 'Disclosure'],
      [/\bCosmico\b/g, 'Cosmic'],
      [/\bAntigo\b/g, 'Ancient'],
      [/\bCivilizacoes\b/g, 'Civilizations'],
      [/\bCivilizacao\b/g, 'Civilization'],
      [/\bConstrutor\b/g, 'Builder'],
      [/\bRaca\b/g, 'Race'],
      [/\bTecnologia\b/g, 'Technology'],
      [/\bBarreira\b/g, 'Barrier'],
      [/\bEnergia\b/g, 'Energy'],
      [/\bLivre\b/g, 'Free'],
      [/\bSecreto\b/g, 'Secret'],
      [/\bEspacial\b/g, 'Space'],
      [/\bPrograma\b/g, 'Program'],
      [/\bTerra\b/g, 'Earth'],
      [/\bLua\b/g, 'Moon'],
      [/\bEstelar\b/g, 'Star'],
      [/\bEstrelas\b/g, 'Stars'],
      [/\bAglomerado\b/g, 'Cluster'],
      [/\bGrade\b/g, 'Grid'],
      [/\bConselho\b/g, 'Council'],
      [/\bFonte\b/g, 'Source'],
      [/\bCriador\b/g, 'Creator'],
      [/\bInfinito\b/g, 'Infinite'],
      [/\bConhecimento\b/g, 'Knowledge'],
      [/\bArquetipo\b/g, 'Archetype'],
      [/\bCura\b/g, 'Healing'],
      [/\bPsiquico\b/g, 'Psychic'],
      [/\bConsciencia\b/g, 'Consciousness'],
      [/\bConsciente\b/g, 'Conscious'],
      [/\bAscensao\b/g, 'Ascension'],
      [/\bDensidade\b/g, 'Density'],
      [/\bLuz\b/g, 'Light'],
      [/\bCorpo\b/g, 'Body'],
      [/\bTempo\b/g, 'Time'],
      [/\bViagem\b/g, 'Travel'],
      [/\bDefesa\b/g, 'Defense'],
      [/\bFrota\b/g, 'Fleet'],
      [/\bMarinha\b/g, 'Navy'],
      [/\bMilitar\b/g, 'Military'],
      [/\bSubterraneo\b/g, 'Underground'],
      [/\bNegro\b/g, 'Black'],
      [/\bAzul\b/g, 'Blue'],
      [/\bDourado\b/g, 'Golden'],
      [/\bGrupo\b/g, 'Group'],
      [/\bAlianca\b/g, 'Alliance'],
      [/\bSociedades\b/g, 'Societies'],
      [/\bSimbolos\b/g, 'Symbols'],
      [/\bOculto\b/g, 'Occult'],
      [/\bMacons\b/g, 'Masons'],
      [/\bLivro\b/g, 'Book'],
      [/\bVida\b/g, 'Life'],
      [/\bAmor\b/g, 'Love'],
      [/\bAtracao\b/g, 'Attraction'],
      [/\bOrganico\b/g, 'Organic'],
      [/\bMisterio\b/g, 'Mystery'],
      [/\bCriacao\b/g, 'Creation'],
      [/\bRetorno\b/g, 'Return'],
      [/\bSofrimento\b/g, 'Suffering'],
      [/\bPortal\b/g, 'Gateway'],
      [/\bGuardioes\b/g, 'Guardians'],
      [/\bSol\b/g, 'Sun'],
      [/\bEvento\b/g, 'Event'],
      [/\bTempestade\b/g, 'Storm'],
      [/\bUltra secreto\b/g, 'Top Secret'],
      [/\bUno\b/g, 'One']
    ];

    return replacements.reduce((current, [pattern, replacement]) => {
      return current.replace(pattern, replacement);
    }, text).replace(/\s{2,}/g, ' ').trim();
  }

  private translatePhrase(text: string | null | undefined): string {
    if (!text) {
      return '';
    }

    const exactTranslations: Record<string, string> = {
      'Great Awakening': 'Grande Despertar',
      'Great Solar Flash': 'Grande Flash Solar',
      'Secret Space Program': 'Programa Espacial Secreto',
      'Ancient Builder Race': 'Raca Construtora Antiga',
      'Ley Lines': 'Linhas de Ley',
      'Earth Grid': 'Grade da Terra',
      'The Matrix': 'A Matriz',
      'Simulation Matrix': 'Matriz da Simulacao',
      'One Infinite Creator': 'Criador Infinito Unico',
      'Return to Source': 'Retorno a Fonte',
      'Rainbow Body': 'Corpo Arco-Iris',
      'Blue Avians': 'Aviarios azuis',
      'Sphere Being Alliance': 'Alianca dos Seres Esfera',
      'Deep State': 'Estado profundo',
      'Disclosure': 'Divulgacao',
      'Full Disclosure': 'Divulgacao completa',
      'Free Energy': 'Energia livre',
      'Zero Point Energy': 'Energia de ponto zero',
      'Law of One': 'Lei do Uno',
      'Law of Attraction': 'Lei da atracao',
      'Flower of Life': 'Flor da Vida',
      'Tree of Life': 'Arvore da Vida',
      'Book of Enoch': 'Livro de Enoque',
      'Book of Thoth': 'Livro de Thoth',
      'Black Cube': 'Cubo negro',
      'Moon': 'Lua',
      'The Sun': 'O Sol',
      'The Event': 'O Evento',
      'The Storm': 'A Tempestade',
      'Top Secret': 'Ultra secreto'
    };

    const exact = exactTranslations[text];
    if (exact) {
      return exact;
    }

    const replacements: Array<[RegExp, string]> = [
      [/\bGreat Awakening\b/g, 'Grande Despertar'],
      [/\bGreat Solar Flash\b/g, 'Grande Flash Solar'],
      [/\bSecret Space Program\b/g, 'Programa Espacial Secreto'],
      [/\bAncient Builder Race\b/g, 'Raca Construtora Antiga'],
      [/\bArtificial Intelligence\b/g, 'Inteligencia artificial'],
      [/\bA\.I\./g, 'IA'],
      [/\bAI\b/g, 'IA'],
      [/\bMatrix\b/g, 'Matriz'],
      [/\bSimulation\b/g, 'Simulacao'],
      [/\bDisclosure\b/g, 'Divulgacao'],
      [/\bCosmic\b/g, 'Cosmico'],
      [/\bSolar\b/g, 'Solar'],
      [/\bFlash\b/g, 'Flash'],
      [/\bAncient\b/g, 'Antigo'],
      [/\bCivilizations\b/g, 'Civilizacoes'],
      [/\bCivilization\b/g, 'Civilizacao'],
      [/\bBuilder\b/g, 'Construtor'],
      [/\bRace\b/g, 'Raca'],
      [/\bTechnology\b/g, 'Tecnologia'],
      [/\bBarrier\b/g, 'Barreira'],
      [/\bEnergy\b/g, 'Energia'],
      [/\bFree\b/g, 'Livre'],
      [/\bSecret\b/g, 'Secreto'],
      [/\bSpace\b/g, 'Espacial'],
      [/\bProgram\b/g, 'Programa'],
      [/\bEarth\b/g, 'Terra'],
      [/\bMoon\b/g, 'Lua'],
      [/\bStar\b/g, 'Estelar'],
      [/\bStars\b/g, 'Estrelas'],
      [/\bCluster\b/g, 'Aglomerado'],
      [/\bGrid\b/g, 'Grade'],
      [/\bCouncil\b/g, 'Conselho'],
      [/\bSource\b/g, 'Fonte'],
      [/\bCreator\b/g, 'Criador'],
      [/\bInfinite\b/g, 'Infinito'],
      [/\bUniversal\b/g, 'Universal'],
      [/\bKnowledge\b/g, 'Conhecimento'],
      [/\bArchetype\b/g, 'Arquetipo'],
      [/\bHealing\b/g, 'Cura'],
      [/\bPsychic\b/g, 'Psiquico'],
      [/\bConsciousness\b/g, 'Consciencia'],
      [/\bConscious\b/g, 'Consciente'],
      [/\bAscension\b/g, 'Ascensao'],
      [/\bDensity\b/g, 'Densidade'],
      [/\bLight\b/g, 'Luz'],
      [/\bBody\b/g, 'Corpo'],
      [/\bTime\b/g, 'Tempo'],
      [/\bTravel\b/g, 'Viagem'],
      [/\bTemporal\b/g, 'Temporal'],
      [/\bDefense\b/g, 'Defesa'],
      [/\bFleet\b/g, 'Frota'],
      [/\bNavy\b/g, 'Marinha'],
      [/\bMilitary\b/g, 'Militar'],
      [/\bUnderground\b/g, 'Subterraneo'],
      [/\bBases\b/g, 'Bases'],
      [/\bBase\b/g, 'Base'],
      [/\bDark\b/g, 'Negro'],
      [/\bBlue\b/g, 'Azul'],
      [/\bBlack\b/g, 'Negro'],
      [/\bGolden\b/g, 'Dourado'],
      [/\bGroup\b/g, 'Grupo'],
      [/\bAlliance\b/g, 'Alianca'],
      [/\bSocieties\b/g, 'Sociedades'],
      [/\bSymbols\b/g, 'Simbolos'],
      [/\bHidden\b/g, 'Oculto'],
      [/\bOccult\b/g, 'Oculto'],
      [/\bMasons\b/g, 'Macons'],
      [/\bBook\b/g, 'Livro'],
      [/\bLife\b/g, 'Vida'],
      [/\bLove\b/g, 'Amor'],
      [/\bAttraction\b/g, 'Atracao'],
      [/\bNon-Interference\b/g, 'Nao interferencia'],
      [/\bOrganic\b/g, 'Organico'],
      [/\bNatural\b/g, 'Natural'],
      [/\bMystery\b/g, 'Misterio'],
      [/\bCreation\b/g, 'Criacao'],
      [/\bReturn\b/g, 'Retorno'],
      [/\bEscape\b/g, 'Escape'],
      [/\bSuffering\b/g, 'Sofrimento'],
      [/\bGateway\b/g, 'Portal'],
      [/\bGuardians\b/g, 'Guardioes'],
      [/\bSun\b/g, 'Sol'],
      [/\bEvent\b/g, 'Evento'],
      [/\bStorm\b/g, 'Tempestade'],
      [/\bTop\b/g, 'Ultra'],
      [/\bSecret\b/g, 'secreto'],
      [/\bOne\b/g, 'Uno'],
      [/\band\b/g, 'e'],
      [/\bof\b/g, 'de'],
      [/\bfrom\b/g, 'de'],
      [/\bto\b/g, 'para'],
      [/\bin\b/g, 'em'],
      [/\bon\b/g, 'em'],
      [/\bwith\b/g, 'com'],
      [/\bthe\b/gi, '']
    ];

    return replacements.reduce((current, [pattern, replacement]) => {
      return current.replace(pattern, replacement);
    }, text).replace(/\s{2,}/g, ' ').trim();
  }

  ngAfterViewInit(): void {
    this.initThreeGlobe();
  }

  ngOnDestroy(): void {
    this.cancelViewAnimation();
    if (this.globeAnimationFrame !== null) {
      cancelAnimationFrame(this.globeAnimationFrame);
    }
    if (this.threeAnimationFrame !== null) {
      cancelAnimationFrame(this.threeAnimationFrame);
    }
    this.resizeObserver?.disconnect();
    this.controls?.dispose();
    this.renderer?.dispose();
  }

  private loadDiscoveredNodes(): void {
    try {
      const stored = window.localStorage.getItem('awakenmap.discoveredNodes');
      const ids = stored ? JSON.parse(stored) : [];

      if (Array.isArray(ids)) {
        this.discoveredNodeIds.set(new Set(ids.filter((id): id is string => typeof id === 'string')));
      }
    } catch {
      this.discoveredNodeIds.set(new Set());
    }
  }

  private markNodeDiscovered(nodeId: string): void {
    const next = new Set(this.discoveredNodeIds());

    if (next.has(nodeId)) {
      return;
    }

    next.add(nodeId);
    this.discoveredNodeIds.set(next);

    try {
      window.localStorage.setItem('awakenmap.discoveredNodes', JSON.stringify([...next]));
    } catch {
      // O contador continua funcionando na sessao mesmo se o navegador bloquear storage.
    }
  }

  categoryById(id: string): Category | undefined {
    return this.data()?.categories.find((category) => category.id === id);
  }

  nodeById(id: string): MapNode | undefined {
    return this.data()?.nodes.find((node) => node.id === id);
  }

  edgePath(edge: MapEdge): string {
    const source = this.nodeById(edge.source);
    const target = this.nodeById(edge.target);

    if (!source || !target) {
      return '';
    }

    const sourcePosition = this.nodeRenderPosition(source);
    const targetPosition = this.nodeRenderPosition(target);

    if (this.isCleanOverview()) {
      const midX = (sourcePosition.x + targetPosition.x) / 2;
      const midY = (sourcePosition.y + targetPosition.y) / 2;
      const length = Math.hypot(midX, midY) || 1;
      const lift = 90 + Math.hypot(targetPosition.x - sourcePosition.x, targetPosition.y - sourcePosition.y) * 0.08;
      const controlX = midX + (midX / length) * lift;
      const controlY = midY + (midY / length) * lift;

      return `M ${sourcePosition.x} ${sourcePosition.y} Q ${controlX} ${controlY} ${targetPosition.x} ${targetPosition.y}`;
    }

    const dx = targetPosition.x - sourcePosition.x;
    const curve = Math.max(-90, Math.min(90, dx * 0.18));
    const midY = (sourcePosition.y + targetPosition.y) / 2 + curve;

    return `M ${sourcePosition.x} ${sourcePosition.y} Q ${(sourcePosition.x + targetPosition.x) / 2} ${midY} ${targetPosition.x} ${targetPosition.y}`;
  }

  edgeLabelPosition(edge: MapEdge): { x: number; y: number } {
    const source = this.nodeById(edge.source);
    const target = this.nodeById(edge.target);
    const sourcePosition = source ? this.nodeRenderPosition(source) : { x: 0, y: 0 };
    const targetPosition = target ? this.nodeRenderPosition(target) : { x: 0, y: 0 };

    return {
      x: (sourcePosition.x + targetPosition.x) / 2,
      y: (sourcePosition.y + targetPosition.y) / 2 - 8
    };
  }

  edgeClass(edge: MapEdge): string {
    const selectedId = this.selectedNodeId();
    const touchesSelected = edge.source === selectedId || edge.target === selectedId;
    return `${touchesSelected ? 'edge is-related' : 'edge'} layer-${this.edgeVisualLayer(edge)}`;
  }

  isCleanOverview(): boolean {
    return this.mapMode() === 'explore'
      && !this.focusedNodeId()
      && !this.query().trim();
  }

  isGlobeOverview(): boolean {
    return (this.mapMode() === 'explore' || this.mapMode() === 'total')
      && !this.focusedNodeId()
      && !this.query().trim();
  }

  edgeWidth(edge: MapEdge): number {
    if (this.mapMode() === 'explore' && !this.selectedNodeId()) {
      return Math.min(1.45, 0.45 + edge.strength * 0.18);
    }

    return Math.min(3.2, 0.9 + edge.strength * 0.42);
  }

  nodeRadius(node: MapNode): number {
    if (this.mapMode() === 'explore' && !this.focusedNodeId() && !this.query().trim()) {
      return 7 + node.importance * 1.55;
    }

    const farScale = this.viewBox().width > 3600 ? 0.72 : this.viewBox().width > 2200 ? 0.86 : 1;
    return (18 + node.importance * 5) * farScale;
  }

  nodeVisualScale(node: MapNode): number {
    if (node.id === this.focusedNodeId()) {
      return 1.18;
    }

    if (node.id === this.selectedNodeId()) {
      return 1.08;
    }

    return 1;
  }

  nodeVisualLayer(node: MapNode): VisualLayer {
    const canonical = this.canonicalNode(node);
    const searchable = `${canonical.id} ${canonical.title} ${canonical.titleOriginal ?? ''} ${canonical.type} ${canonical.summary} ${canonical.categoryId}`.toLowerCase();
    const physicalNodeIds = new Set([
      'n-lote1-giza-complex',
      'n-lote1-sphinx',
      'n-pyramids',
      'n-lote1-ancient-egypt',
      'n-lote1-gobekli-tepe',
      'n-lote1-bosnian-pyramid',
      'n-lote1-japanese-underwater-pyramids',
      'n-lote1-stone-circles',
      'n-lote1-megalithic-sites',
      'n-lote1-crop-circles',
      'n-atlantis-lemuria-mu',
      'n-lote1-atlantis',
      'n-lote1-lemuria',
      'n-antarctica',
      'n-neuschwabenland',
      'n-operation-highjump',
      'n-argentina',
      'n-area-51',
      'n-lote1-dulce-base',
      'n-sandia-dulce',
      'n-dumbs',
      'n-lote1-deep-underground-military-bases',
      'n-lote1-secret-underground-bases',
      'n-lote1-underground-base',
      'n-inner-earth',
      'n-lote1-hollow-earth',
      'n-lote1-inner-earth-civilization',
      'n-lote1-agartha',
      'n-lote1-transparent-ancient-caverns',
      'n-lote1-library-of-porthologos',
      'n-lote1-ancient-ruins'
    ]);

    if (physicalNodeIds.has(canonical.id)) {
      return 'physical';
    }

    if (
      searchable.includes('orion')
      || searchable.includes('sirius')
      || searchable.includes('pleiades')
      || searchable.includes('plÃªiades')
      || searchable.includes('pleiadian')
      || searchable.includes('arctur')
      || searchable.includes('fonte')
      || searchable.includes('source')
      || searchable.includes('cosmic')
      || searchable.includes('cosmica')
      || searchable.includes('solar')
      || searchable.includes('sun')
      || searchable.includes('venus')
      || searchable.includes('saturn')
      || searchable.includes('moon')
      || searchable.includes('mars')
      || searchable.includes('cydonia')
      || searchable.includes('maldek')
      || searchable.includes('planet')
      || searchable.includes('star')
      || searchable.includes('density')
      || searchable.includes('densidade')
      || searchable.includes('extraterrestrial')
      || searchable.includes('beings')
      || searchable.includes('elohim')
      || searchable.includes('annunaki')
      || searchable.includes('anunnaki')
      || searchable.includes('starseed')
      || searchable.includes('wanderers')
    ) {
      return 'cosmic';
    }

    if (
      searchable.includes('ley')
      || searchable.includes('earth grid')
      || searchable.includes('grid')
      || searchable.includes('geometria')
      || searchable.includes('geometry')
      || searchable.includes('hermet')
      || searchable.includes('alchemy')
      || searchable.includes('alquimia')
      || searchable.includes('consciousness')
      || searchable.includes('consciencia')
      || searchable.includes('matrix')
      || searchable.includes('simulation')
      || searchable.includes('simulacao')
      || searchable.includes('cabala')
      || searchable.includes('kabbalah')
      || searchable.includes('gnostic')
      || searchable.includes('gnosticismo')
      || searchable.includes('free energy')
      || searchable.includes('energia livre')
      || searchable.includes('numerologia')
      || searchable.includes('astrologia')
      || searchable.includes('tarot')
      || searchable.includes('sincronic')
      || searchable.includes('arquet')
      || searchable.includes('occult')
      || searchable.includes('ocult')
      || searchable.includes('mistic')
      || searchable.includes('resonance')
      || searchable.includes('ressonancia')
      || searchable.includes('tempo')
      || searchable.includes('time')
      || searchable.includes('duality')
      || searchable.includes('dualidade')
      || searchable.includes('unity')
      || searchable.includes('unidade')
      || searchable.includes('egregor')
      || searchable.includes('theory')
      || searchable.includes('teoria')
      || searchable.includes('system')
      || searchable.includes('sistema')
      || searchable.includes('symbol')
      || searchable.includes('simbolo')
      || searchable.includes('school')
      || searchable.includes('tradition')
      || searchable.includes('tradicao')
    ) {
      return 'conceptual';
    }

    if (
      searchable.includes('ceres')
      || searchable.includes('asteroid')
      || searchable.includes('cinturao')
      || searchable.includes('maldek')
      || searchable.includes('venus')
      || searchable.includes('moon')
      || searchable.includes('mars')
      || searchable.includes('cydonia')
      || searchable.includes('planet')
    ) {
      return 'cosmic';
    }

    if (
      searchable.includes('location')
      || searchable.includes('terra')
      || searchable.includes('earth')
      || searchable.includes('area 51')
      || searchable.includes('base')
      || searchable.includes('site')
      || searchable.includes('cidade')
      || searchable.includes('city')
      || searchable.includes('giza')
      || searchable.includes('egito')
      || searchable.includes('egypt')
      || searchable.includes('pyramid')
      || searchable.includes('piramide')
      || searchable.includes('sphinx')
      || searchable.includes('ruins')
      || searchable.includes('gobekli')
      || searchable.includes('stone circles')
      || searchable.includes('megalithic')
      || searchable.includes('crop circles')
      || searchable.includes('underwater')
      || searchable.includes('atlant')
      || searchable.includes('lemuria')
    ) {
      return 'physical';
    }

    if (
      canonical.categoryId === 'solar-flash'
      || canonical.categoryId === 'ssp'
      || canonical.categoryId === 'source'
      || searchable.includes('solar')
      || searchable.includes('cosmic')
      || searchable.includes('cosmica')
      || searchable.includes('orion')
      || searchable.includes('sirius')
      || searchable.includes('pleiades')
      || searchable.includes('plêiades')
      || searchable.includes('fonte')
      || searchable.includes('planet')
      || searchable.includes('star')
      || searchable.includes('density')
      || searchable.includes('venus')
      || searchable.includes('saturn')
      || searchable.includes('moon')
      || searchable.includes('extraterrestrial')
      || searchable.includes('beings')
    ) {
      return 'cosmic';
    }

    if (
      searchable.includes('thoth')
      || searchable.includes('hermet')
      || searchable.includes('atlanti')
      || searchable.includes('mason')
      || searchable.includes('maçon')
      || searchable.includes('secret')
      || searchable.includes('occult')
      || searchable.includes('ocult')
      || searchable.includes('gnostic')
      || searchable.includes('cabala')
      || searchable.includes('kabbalah')
      || searchable.includes('templar')
      || searchable.includes('rosacruz')
      || searchable.includes('sociedade')
    ) {
      return 'occult';
    }

    return 'conceptual';
  }

  private canonicalNode(node: MapNode): MapNode {
    return this.rawData?.nodes.find((item) => item.id === node.id) ?? node;
  }

  nodeRenderPosition(node: MapNode): { x: number; y: number } {
    if (!this.isCleanOverview()) {
      return node.position;
    }

    return this.projectGlobePoint(node);
  }

  nodeVisibility(node: MapNode): number {
    if (!this.isCleanOverview()) {
      return 1;
    }

    return this.projectGlobePoint(node).visible ? 1 : 0.16;
  }

  globeSurfaceTransform(): string {
    return `rotate(${this.globeRotation() * 0.16})`;
  }

  globeTextureTransform(): string {
    const textureWidth = 1860;
    const offset = (((this.globeRotation() / 360) * textureWidth + textureWidth / 2) % textureWidth) - textureWidth / 2;
    return `translate(${offset}, 0)`;
  }

  nodeTone(node: MapNode): string {
    if (!this.isCleanOverview()) {
      return this.nodeVisualLayer(node);
    }

    const tones = ['blue', 'green', 'gold', 'purple', 'red', 'cyan'];
    const hash = Array.from(node.id).reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return tones[hash % tones.length];
  }

  nodeGradient(node: MapNode): string {
    return this.isCleanOverview() ? this.nodeTone(node) : this.nodeVisualLayer(node);
  }

  private edgeVisualLayer(edge: MapEdge): VisualLayer {
    const source = this.nodeById(edge.source);
    const target = this.nodeById(edge.target);
    const sourceLayer = source ? this.nodeVisualLayer(source) : 'conceptual';
    const targetLayer = target ? this.nodeVisualLayer(target) : 'conceptual';

    if (sourceLayer === targetLayer) {
      return sourceLayer;
    }

    if (sourceLayer === 'cosmic' || targetLayer === 'cosmic') {
      return 'cosmic';
    }

    if (sourceLayer === 'occult' || targetLayer === 'occult') {
      return 'occult';
    }

    if (sourceLayer === 'physical' && targetLayer === 'physical') {
      return 'physical';
    }

    return 'conceptual';
  }

  focusSceneRadius(): number {
    const box = this.viewBox();
    return Math.min(Math.max(box.width, box.height) * 0.9, 620);
  }

  focusContentBox(): { x: number; y: number; width: number; height: number } {
    const box = this.viewBox();
    const width = box.width * 0.92;
    const height = box.height * 0.84;

    return {
      x: -width / 2,
      y: -height / 2,
      width,
      height
    };
  }

  focusOrbitPosition(index: number, total: number): { x: number; y: number } {
    const radius = this.focusSceneRadius() * 0.76;
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / Math.max(total, 1);

    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius
    };
  }

  focusSceneViewBox(): string {
    const radius = this.focusSceneRadius();
    return `${-radius} ${-radius} ${radius * 2} ${radius * 2}`;
  }

  shouldShowNodeLabel(node: MapNode): boolean {
    const width = this.viewBox().width;

    if (node.id === this.selectedNodeId() || this.focusedNodeId() === node.id) {
      return true;
    }

    if (this.mapMode() === 'explore' && !this.focusedNodeId()) {
      return node.importance >= 4 || this.nodeDegree(node.id) >= 8;
    }

    if (width > 4200) {
      return node.importance >= 5;
    }

    if (width > 2600) {
      return node.importance >= 4;
    }

    if (width > 1600) {
      return node.importance >= 3;
    }

    return true;
  }

  setLayer(layerId: string): void {
    this.activeLayer.set(layerId);
    this.focusedNodeId.set(null);
    setTimeout(() => this.resetView());
  }

  toggleGlobeLayer(layer: VisualLayer, checked: boolean): void {
    const next = new Set(this.enabledGlobeLayers());
    if (checked) {
      next.add(layer);
    } else {
      next.delete(layer);
    }
    this.enabledGlobeLayers.set(next);
    this.selectedNodeId.set('');
    this.rebuildThreeGlobe();
  }

  isGlobeLayerEnabled(layer: VisualLayer): boolean {
    return this.enabledGlobeLayers().has(layer);
  }

  toggleSearch(): void {
    this.searchOpen.set(!this.searchOpen());
    if (this.searchOpen()) {
      this.filterOpen.set(false);
      setTimeout(() => this.globeSearchInput?.nativeElement.focus());
    }
  }

  toggleFilterPanel(): void {
    this.filterOpen.set(!this.filterOpen());
    if (this.filterOpen()) {
      this.searchOpen.set(false);
    }
  }

  toggleAutoRotate(): void {
    this.autoRotate.set(!this.autoRotate());
    if (this.controls) {
      this.controls.autoRotate = this.autoRotate();
    }
  }

  clearGlobeSelection(): void {
    this.selectedNodeId.set('');
    this.activeContext.set(this.text('contextOverview'));
    this.rebuildThreeGlobe();
  }

  threeZoom(direction: 'in' | 'out' | 'reset'): void {
    if (!this.camera || !this.controls) {
      return;
    }

    if (direction === 'reset') {
      this.camera.position.set(0, 0, this.globeDefaultCameraDistance());
      this.controls.target.set(0, 0, 0);
      this.controls.update();
      return;
    }

    const factor = direction === 'in' ? 0.86 : 1.16;
    this.camera.position.multiplyScalar(factor);
    const distance = this.camera.position.length();
    if (distance < this.controls.minDistance) {
      this.camera.position.setLength(this.controls.minDistance);
    }
    if (distance > this.controls.maxDistance) {
      this.camera.position.setLength(this.controls.maxDistance);
    }
    this.controls.update();
  }

  searchGlobe(): void {
    const term = this.globeSearch().trim().toLowerCase();
    if (!term) {
      return;
    }

    const node = this.visibleNodes().find((item) => {
      return `${item.title} ${item.titleOriginal ?? ''} ${item.summary}`.toLowerCase().includes(term);
    });

    if (node) {
      this.selectedNodeId.set(node.id);
      this.activeContext.set(`${node.title} ${this.text('locatedOnGlobe')}`);
      this.rebuildThreeGlobe();
    }
  }

  globeLayerLabel(layer: VisualLayer): string {
    if (layer === 'physical') return this.language() === 'pt' ? 'Mundo' : 'World';
    if (layer === 'conceptual') return this.language() === 'pt' ? 'Conhecimento' : 'Knowledge';
    if (layer === 'occult') return this.language() === 'pt' ? 'Oculto' : 'Occult';
    return this.language() === 'pt' ? 'Cosmico' : 'Cosmic';
  }

  globeLayerDescription(layer: VisualLayer): string {
    if (layer === 'physical') return this.language() === 'pt' ? 'Locais e pontos geograficos' : 'Places and geographic points';
    if (layer === 'conceptual') return this.language() === 'pt' ? 'Ideias, simbolos e tradicoes' : 'Ideas, symbols and traditions';
    if (layer === 'occult') return this.language() === 'pt' ? 'Sociedades, simbolos e mitos' : 'Societies, symbols and myths';
    return this.language() === 'pt' ? 'Entidades, estrelas e planos superiores' : 'Entities, stars and higher planes';
  }

  globeLayerClass(layer: VisualLayer): string {
    return `layer-${layer}`;
  }

  setMode(mode: 'explore' | 'total'): void {
    this.mapMode.set(mode);
    if (mode === 'explore') {
      this.deepMode.set(false);
    }
    this.focusedNodeId.set(null);
    this.expandedConnections.set(false);
    this.clearGlobeSelection();
    this.rebuildThreeGlobe();
    setTimeout(() => this.resetView());
  }

  toggleDeepMode(): void {
    this.deepMode.set(!this.deepMode());
    this.rebuildThreeGlobe();
  }

  toggleExpandedConnections(): void {
    this.expandedConnections.set(!this.expandedConnections());
  }

  iconKind(node: MapNode): string {
    if (node.type.includes('archive')) return 'book';
    if (node.type.includes('finance') || node.id.includes('gold') || node.id.includes('crypto')) return 'coin';
    if (node.type.includes('portal') || node.id.includes('stargate')) return 'portal';
    if (node.type.includes('planet') || node.type.includes('moon') || node.id.includes('saturn') || node.id.includes('venus')) return 'planet';
    if (node.type.includes('people') || node.type.includes('group') || node.type.includes('alliance')) return 'people';
    if (node.type.includes('substance')) return 'drop';
    if (node.type.includes('operation') || node.type.includes('program')) return 'shield';
    if (node.type.includes('teaching') || node.type.includes('theory') || node.type.includes('framework')) return 'book';
    if (node.type.includes('technology') || node.type.includes('device')) return 'gear';
    if (node.type.includes('time') || node.id.includes('timeline') || node.id.includes('travel')) return 'hourglass';
    if (node.type.includes('field') || node.id.includes('schumann')) return 'wave';
    if (node.type.includes('location')) return 'pin';
    if (node.type.includes('person')) return 'person';
    if (node.type.includes('beings') || node.type.includes('entity')) return 'being';
    if (node.type.includes('healing') || node.id.includes('organic')) return 'leaf';
    if (node.id.includes('reptilian')) return 'reptile';
    if (node.id.includes('solar') || node.id.includes('flash')) return 'sun';
    if (node.id.includes('meditation') || node.id.includes('dzogchen') || node.id.includes('rainbow')) return 'lotus';
    if (node.id.includes('space') || node.id.includes('warden') || node.id.includes('stargates')) return 'ship';
    if (node.id.includes('antarctica') || node.id.includes('earth')) return 'mountain';
    if (node.id.includes('ai') || node.id.includes('matrix')) return 'chip';
    if (node.id.includes('tesla') || node.id.includes('energy') || node.id.includes('wireless')) return 'bolt';
    if (node.id.includes('cabal') || node.id.includes('q-anon') || node.id.includes('mkultra')) return 'eye';
    if (node.categoryId === 'esoteric-practice' || node.categoryId === 'source') return 'spark';
    if (node.categoryId === 'ancient-builder') return 'pyramid';
    return 'orb';
  }

  toggleCategory(categoryId: string, checked: boolean): void {
    const next = new Set(this.enabledCategories());
    checked ? next.add(categoryId) : next.delete(categoryId);
    this.enabledCategories.set(next);
    if (!this.focusedNodeId()) {
      setTimeout(() => this.resetView());
    }
  }

  selectNode(nodeId: string): void {
    const node = this.nodeById(nodeId);

    if (node) {
      if (this.isGlobeOverview()) {
        if (this.selectedNodeId() === nodeId) {
          this.clearGlobeSelection();
          return;
        }

        this.selectedNodeId.set(nodeId);
        this.markNodeDiscovered(nodeId);
        this.activeContext.set(`${this.globeLayerLabel(this.nodeVisualLayer(node))} / ${node.title}`);
        this.rebuildThreeGlobe();
        return;
      }

      this.selectedNodeId.set(nodeId);
      const category = this.categoryById(node.categoryId);
      this.activeContext.set(`${category?.name ?? this.text('mapFallback')} / ${node.type}`);

      if (this.focusedNodeId() === nodeId) {
        this.focusedNodeId.set(null);
        this.resetView();
        return;
      }

      this.focusedNodeId.set(nodeId);
      this.expandedConnections.set(false);
      this.focusNode(node);
    }
  }

  startTrail(trail: Trail): void {
    const first = trail.steps[0];
    if (!first) {
      return;
    }

    this.selectNode(first.nodeId);
    this.activeContext.set(trail.description);
  }

  zoom(multiplier: number): void {
    const box = this.viewBox();
    const centerX = box.x + box.width / 2;
    const centerY = box.y + box.height / 2;
    const width = Math.max(420, Math.min(7600, box.width * multiplier));
    const height = Math.max(300, Math.min(5200, box.height * multiplier));

    this.setViewBoxAnimated({
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height
    });
  }

  zoomToNode(node: MapNode, multiplier: number): void {
    const width = Math.max(420, Math.min(7600, 820 * multiplier));
    const height = Math.max(300, Math.min(5200, 620 * multiplier));

    this.setViewBoxAnimated({
      x: node.position.x - width / 2,
      y: node.position.y - height / 2,
      width,
      height
    });
  }

  resetView(): void {
    this.focusedNodeId.set(null);
    this.expandedConnections.set(false);
    this.selectedNodeId.set('');
    if (this.mapMode() === 'explore' && !this.query().trim()) {
      this.setViewBoxAnimated({ x: -1280, y: -900, width: 2560, height: 1800 });
      this.activeContext.set(this.text('contextOverview'));
      return;
    }

    const visible = this.visibleNodes();
    const nodes = this.mapMode() === 'explore' && !this.query().trim()
      ? this.overviewNodes(visible)
      : visible;
    this.setViewBoxAnimated(this.fitViewBoxToNodes(nodes));
    this.activeContext.set(this.text('contextOverview'));
  }

  exitFocus(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.resetView();
  }

  closeDetail(): void {
    this.focusedNodeId.set(null);
  }

  /**
   * Aproxima o mapa no assunto escolhido.
   *
   * A regra de produto e simples: primeiro clique aproxima; segundo clique no
   * mesmo assunto volta ao mapa geral. Mantemos isso aqui para facilitar seu
   * estudo depois.
   */
  focusNode(node: MapNode): void {
    this.zoomToNode(node, 0.68);
  }

  topicUrl(node: MapNode): string {
    return `/conteudo/${node.slug}/`;
  }

  /**
   * Calcula a visao geral a partir dos pontos reais.
   *
   * Antes o mapa geral usava um viewBox fixo, entao alguns filtros podiam
   * deixar pontos fora da area confortavel. Agora enquadramos todos os pontos
   * visiveis com uma margem, como um "zoom para caber".
   */
  private fitViewBoxToNodes(nodes: MapNode[]): ViewBox {
    if (!nodes.length) {
      return { x: -1200, y: -820, width: 2400, height: 1640 };
    }

    const xs = nodes.map((node) => node.position.x);
    const ys = nodes.map((node) => node.position.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const padding = 360;
    const width = Math.max(2200, maxX - minX + padding * 2);
    const height = Math.max(1500, maxY - minY + padding * 2);

    return {
      x: minX - padding,
      y: minY - padding,
      width,
      height
    };
  }

  private overviewNodes(nodes: MapNode[]): MapNode[] {
    return nodes
      .slice()
      .sort((a, b) => {
        const scoreA = a.importance * 12 + this.nodeDegree(a.id);
        const scoreB = b.importance * 12 + this.nodeDegree(b.id);
        return scoreB - scoreA;
      })
      .slice(0, 24);
  }

  private isKnowledgeGlobeNode(node: MapNode): boolean {
    if (this.nodeVisualLayer(node) !== 'conceptual') {
      return false;
    }

    const canonical = this.canonicalNode(node);
    const text = `${canonical.id} ${canonical.title} ${canonical.titleOriginal ?? ''} ${canonical.type} ${canonical.summary} ${canonical.categoryId}`.toLowerCase();
    const explicitKnowledgeTerms = [
      'hermet', 'alchemy', 'alquimia', 'consciousness', 'consciencia', 'matrix',
      'simulation', 'simulacao', 'ley', 'geometr', 'cabala', 'kabbalah',
      'gnostic', 'free energy', 'energia livre', 'numerolog', 'astrolog',
      'tarot', 'sincronic', 'arquet', 'occult', 'ocult', 'mistic',
      'resonance', 'ressonancia', 'tempo', 'time', 'duality', 'dualidade',
      'unity', 'unidade', 'egregor', 'symbol', 'simbolo', 'cymatics',
      'chakras', 'auras', 'meditation', 'meditacao', 'dzogchen', 'bardo'
    ];

    return explicitKnowledgeTerms.some((term) => text.includes(term))
      || node.importance >= 4
      || this.nodeDegree(node.id) >= 7;
  }

  private isCosmicGlobeNode(node: MapNode): boolean {
    if (this.nodeVisualLayer(node) !== 'cosmic') {
      return false;
    }

    const canonical = this.canonicalNode(node);
    const text = `${canonical.id} ${canonical.title} ${canonical.titleOriginal ?? ''} ${canonical.type} ${canonical.summary} ${canonical.categoryId}`.toLowerCase();
    const explicitCosmicTerms = [
      'orion', 'sirius', 'pleiades', 'plêiades', 'pleiadian', 'arctur',
      'fonte', 'source', 'cosmic', 'cosmica', 'solar', 'sun', 'venus',
      'saturn', 'moon', 'mars', 'cydonia', 'maldek', 'planet', 'star',
      'density', 'densidade', 'extraterrestrial', 'beings', 'elohim',
      'annunaki', 'anunnaki', 'starseed', 'wanderers'
    ];

    return explicitCosmicTerms.some((term) => text.includes(term))
      || node.importance >= 4
      || this.nodeDegree(node.id) >= 8;
  }

  private shouldShowNodeInGlobeBase(node: MapNode, allowedLayers: Set<VisualLayer>): boolean {
    const layer = this.nodeVisualLayer(node);

    if (this.isAwakeningRootNode(node)) {
      return allowedLayers.has(layer);
    }

    if (!allowedLayers.has(layer)) {
      return false;
    }

    if (this.mapMode() === 'total') {
      if (this.deepMode()) {
        return layer === 'physical'
          || node.importance >= 2
          || this.nodeDegree(node.id) >= 3
          || this.isKnowledgeGlobeNode(node)
          || this.isCosmicGlobeNode(node);
      }

      return layer === 'physical'
        || node.importance >= 3
        || this.nodeDegree(node.id) >= 5
        || this.isKnowledgeGlobeNode(node)
        || this.isCosmicGlobeNode(node);
    }

    return layer === 'physical' || this.isKnowledgeGlobeNode(node) || this.isCosmicGlobeNode(node);
  }

  private globeEdgeLimit(selectedId: string): number {
    if (selectedId) {
      return this.mapMode() === 'total' ? 14 : 8;
    }

    if (this.mapMode() === 'total') {
      return this.deepMode() ? 72 : 36;
    }

    return 10;
  }

  pointerDown(event: PointerEvent): void {
    event.preventDefault();
    this.cancelViewAnimation();
    const canvas = event.currentTarget as SVGSVGElement;
    this.dragging = true;
    this.lastClientPointer = { x: event.clientX, y: event.clientY };
    this.lastPointer = this.pointerToViewBox(event, canvas);
    canvas.setPointerCapture(event.pointerId);
  }

  pointerMove(event: PointerEvent): void {
    if (!this.dragging || !this.lastPointer) {
      return;
    }

    event.preventDefault();
    const canvas = event.currentTarget as SVGSVGElement;

    if (this.isGlobeOverview() && this.lastClientPointer) {
      const dx = event.clientX - this.lastClientPointer.x;
      this.globeRotation.update((rotation) => rotation + dx * 0.34);
      this.lastClientPointer = { x: event.clientX, y: event.clientY };
      return;
    }

    const point = this.pointerToViewBox(event, canvas);
    const box = this.viewBox();

    this.viewBox.set({
      ...box,
      x: box.x - (point.x - this.lastPointer.x),
      y: box.y - (point.y - this.lastPointer.y)
    });
  }

  pointerUp(event: PointerEvent): void {
    const canvas = event.currentTarget as SVGSVGElement;
    this.dragging = false;
    this.lastPointer = null;
    this.lastClientPointer = null;
    canvas.releasePointerCapture(event.pointerId);
  }

  wheel(event: WheelEvent): void {
    event.preventDefault();
    const box = this.viewBox();
    const multiplier = event.deltaY > 0 ? 1.1 : 0.9;
    const centerX = box.x + box.width / 2;
    const centerY = box.y + box.height / 2;
    const width = Math.max(420, Math.min(7600, box.width * multiplier));
    const height = Math.max(300, Math.min(5200, box.height * multiplier));

    this.cancelViewAnimation();
    this.viewBox.set({
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height
    });
  }

  relatedNode(edge: MapEdge): MapNode | undefined {
    const selected = this.selectedNode();
    const relatedId = edge.source === selected?.id ? edge.target : edge.source;
    return this.nodeById(relatedId);
  }

  threeGlobeClick(event: MouseEvent): void {
    if (!this.renderer || !this.camera || !this.globeHost || !this.isGlobeOverview()) {
      return;
    }

    const rect = this.globeHost.nativeElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const hits = this.raycaster.intersectObjects(this.clickablePointMeshes, false);
    const nodeId = hits[0]?.object.userData['nodeId'];

    if (typeof nodeId === 'string') {
      this.zone.run(() => this.selectNode(nodeId));
    }
  }

  threeGlobePointerMove(event: PointerEvent): void {
    if (!this.renderer || !this.camera || !this.globeHost || !this.isGlobeOverview()) {
      return;
    }

    const rect = this.globeHost.nativeElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const hits = this.raycaster.intersectObjects(this.clickablePointMeshes, false);
    const nodeId = hits[0]?.object.userData['nodeId'];
    const nextId = typeof nodeId === 'string' ? nodeId : '';

    if (nextId !== this.hoveredGlobeNodeId()) {
      this.zone.run(() => {
        this.hoveredGlobeNodeId.set(nextId);
        this.globeHost?.nativeElement.style.setProperty('cursor', nextId ? 'pointer' : 'grab');
        this.rebuildThreeGlobe();
      });
    }
  }

  clearGlobeHover(): void {
    if (!this.hoveredGlobeNodeId()) {
      return;
    }

    this.hoveredGlobeNodeId.set('');
    this.globeHost?.nativeElement.style.setProperty('cursor', 'grab');
    this.rebuildThreeGlobe();
  }

  private initThreeGlobe(): void {
    const host = this.globeHost?.nativeElement;

    if (!host || this.renderer) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
      renderer.setSize(host.clientWidth, host.clientHeight);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      host.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        this.globeCameraFieldOfView(),
        host.clientWidth / Math.max(host.clientHeight, 1),
        0.1,
        100
      );
      camera.position.set(0, 0, this.globeDefaultCameraDistance());

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.enablePan = false;
      controls.minDistance = 4.3;
      controls.maxDistance = 14;
      controls.rotateSpeed = 0.42;
      controls.zoomSpeed = 0.58;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.38;

      const globeGroup = new THREE.Group();
      const pointsGroup = new THREE.Group();
      const linesGroup = new THREE.Group();
      scene.add(globeGroup, linesGroup, pointsGroup);

      const fallbackEarthTexture = this.createEarthTexture(renderer);
      const earthMaterial = new THREE.MeshBasicMaterial({
        map: fallbackEarthTexture,
        color: 0xffffff,
        transparent: false
      });

      new THREE.TextureLoader().load('earth-real.jpg', (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        earthMaterial.map = texture;
        earthMaterial.needsUpdate = true;
        fallbackEarthTexture.dispose();
      }, undefined, () => {
        earthMaterial.map = fallbackEarthTexture;
        earthMaterial.needsUpdate = true;
      });

      const earthMesh = new THREE.Mesh(
        new THREE.SphereGeometry(2.6, 96, 64),
        earthMaterial
      );
      earthMesh.name = 'awakenmap-earth';
      earthMesh.renderOrder = 1;
      globeGroup.add(earthMesh);

      const atmosphereMesh = new THREE.Mesh(
        new THREE.SphereGeometry(2.68, 96, 64),
        new THREE.MeshBasicMaterial({
          color: 0x62c6ff,
          transparent: true,
          opacity: 0.13,
          side: THREE.BackSide,
          blending: THREE.AdditiveBlending
        })
      );
      atmosphereMesh.renderOrder = 2;
      globeGroup.add(atmosphereMesh);

      const outerAtmosphereMesh = new THREE.Mesh(
        new THREE.SphereGeometry(2.72, 96, 64),
        new THREE.MeshBasicMaterial({
          color: 0x4dafff,
          transparent: true,
          opacity: 0.06,
          side: THREE.BackSide,
          blending: THREE.AdditiveBlending
        })
      );
      outerAtmosphereMesh.renderOrder = 3;
      globeGroup.add(outerAtmosphereMesh);

      scene.add(new THREE.AmbientLight(0x86bce8, 1.1));
      const keyLight = new THREE.DirectionalLight(0xffffff, 2.4);
      keyLight.position.set(-2, 1.4, 4);
      scene.add(keyLight);
      const blueLight = new THREE.PointLight(0x3aa8ff, 16, 18);
      blueLight.position.set(3.8, 2.8, 4);
      scene.add(blueLight);
      scene.add(this.createStarField());

      this.renderer = renderer;
      this.scene = scene;
      this.camera = camera;
      this.controls = controls;
      this.globeGroup = globeGroup;
      this.pointsGroup = pointsGroup;
      this.linesGroup = linesGroup;

      this.resizeObserver = new ResizeObserver(() => this.resizeThreeGlobe());
      this.resizeObserver.observe(host);
      this.rebuildThreeGlobe();

      const animate = () => {
        this.threeAnimationFrame = requestAnimationFrame(animate);
        controls.enabled = this.isGlobeOverview();
        controls.autoRotate = this.isGlobeOverview() && this.autoRotate();
        const pulse = 1 + Math.sin(performance.now() * 0.0018) * 0.08;
        pointsGroup.children.forEach((child) => child.scale.setScalar(pulse));
        controls.update();
        renderer.render(scene, camera);
      };

      animate();
    });
  }

  private resizeThreeGlobe(): void {
    const host = this.globeHost?.nativeElement;

    if (!host || !this.renderer || !this.camera) {
      return;
    }

    const width = host.clientWidth;
    const height = Math.max(host.clientHeight, 1);
    this.camera.fov = this.globeCameraFieldOfView();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  private globeDefaultCameraDistance(): number {
    return window.matchMedia('(max-width: 640px)').matches ? 12.4 : 9.15;
  }

  private globeCameraFieldOfView(): number {
    return window.matchMedia('(max-width: 640px)').matches ? 50 : 38;
  }

  private rebuildThreeGlobe(): void {
    if (!this.pointsGroup || !this.linesGroup) {
      return;
    }

    this.clearGroup(this.pointsGroup);
    this.clearGroup(this.linesGroup);
    this.clickablePointMeshes = [];

    const selectedId = this.selectedNodeId();
    const allowedLayers = new Set<VisualLayer>(['physical', 'conceptual', 'cosmic']);
    const baseNodes = this.data()?.nodes.filter((node) => this.shouldShowNodeInGlobeBase(node, allowedLayers)) ?? [];
    const revealIds = new Set<string>();

    if (selectedId) {
      revealIds.add(selectedId);
      for (const edge of this.relatedEdges().slice(0, 8)) {
        revealIds.add(edge.source);
        revealIds.add(edge.target);
      }
    }

    const nodes = this.data()?.nodes
      .filter((node) => {
        const isBase = baseNodes.some((item) => item.id === node.id);
        const isRevealed = revealIds.has(node.id);
        const layer = this.nodeVisualLayer(node);
        return (isBase || isRevealed) && allowedLayers.has(layer) && this.enabledGlobeLayers().has(layer);
      }) ?? [];
    this.globeNodeCount.set(nodes.length);
    const nodeSet = new Set(nodes.map((node) => node.id));
    const nodePositions = new Map<string, THREE.Vector3>();
    const glowTexture = this.createGlowTexture();

    for (const node of nodes) {
      const point = this.globePointForNode(node);
      const layer = this.nodeVisualLayer(node);
      const radius = this.globeLayerRadius(layer);
      const position = layer === 'physical'
        ? this.latLonToVector3(point.lat, point.lon, radius)
        : this.orbitPointForNode(node, radius);
      nodePositions.set(node.id, position);

      const palette = this.isAwakeningRootNode(node)
        ? { core: 0xfff3f0, main: 0xff3b45, halo: 0xff8b8b }
        : this.globeLayerPalette(layer);
      const color = new THREE.Color(palette.main);
      const isSelected = node.id === selectedId;
      const isHovered = node.id === this.hoveredGlobeNodeId();
      const rootScale = this.isAwakeningRootNode(node) ? 1.72 : 1;
      const pointScale = (isSelected ? 1.42 : isHovered ? 1.18 : 1) * rootScale;
      const nodeGroup = new THREE.Group();
      nodeGroup.position.copy(position);
      nodeGroup.userData['nodeId'] = node.id;

      const outerGlow = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: glowTexture,
          color,
          transparent: true,
          opacity: isSelected ? 0.72 : isHovered ? 0.48 : 0.28,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: true
        })
      );
      outerGlow.scale.setScalar((0.46 + node.importance * 0.025) * pointScale);

      const innerGlow = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: glowTexture,
          color: new THREE.Color(palette.halo),
          transparent: true,
          opacity: isSelected ? 0.95 : isHovered ? 0.82 : 0.58,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: true
        })
      );
      innerGlow.scale.setScalar((0.22 + node.importance * 0.014) * pointScale);

      const core = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: glowTexture,
          color: new THREE.Color(palette.core),
          transparent: true,
          opacity: 1,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: true
        })
      );
      core.scale.setScalar((0.07 + node.importance * 0.003) * pointScale);
      core.userData['nodeId'] = node.id;

      nodeGroup.add(outerGlow, innerGlow, core);
      this.pointsGroup.add(nodeGroup);
      this.clickablePointMeshes.push(core);
    }

    const edges = (this.data()?.edges ?? [])
      .filter((edge) => nodeSet.has(edge.source) && nodeSet.has(edge.target))
      .filter((edge) => !selectedId || edge.source === selectedId || edge.target === selectedId)
      .sort((a, b) => b.strength - a.strength)
      .slice(0, this.globeEdgeLimit(selectedId));

    for (const edge of edges) {
      const source = nodePositions.get(edge.source);
      const target = nodePositions.get(edge.target);

      if (!source || !target) {
        continue;
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(this.createGlobeArcPoints(source, target));
      const material = new THREE.LineBasicMaterial({
        color: this.edgeColor(edge),
        transparent: true,
        opacity: selectedId ? 0.42 : 0.2,
        blending: THREE.AdditiveBlending
      });
      this.linesGroup.add(new THREE.Line(geometry, material));
    }
  }

  private createStarField(): THREE.Points {
    const vertices: number[] = [];
    const colors: number[] = [];

    for (let i = 0; i < 520; i += 1) {
      const radius = 18 + Math.random() * 26;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      vertices.push(
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi)
      );
      const shade = 0.45 + Math.random() * 0.45;
      colors.push(shade, shade, shade);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    return new THREE.Points(
      geometry,
      new THREE.PointsMaterial({ size: 0.035, vertexColors: true, transparent: true, opacity: 0.78 })
    );
  }

  private createGlowTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext('2d');

    if (!context) {
      return new THREE.CanvasTexture(canvas);
    }

    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.14, 'rgba(255,255,255,0.96)');
    gradient.addColorStop(0.34, 'rgba(255,255,255,0.54)');
    gradient.addColorStop(0.68, 'rgba(255,255,255,0.16)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  }

  private globeLayerPalette(layer: VisualLayer): { core: number; main: number; halo: number } {
    if (layer === 'conceptual') {
      return {
        core: 0xfff7c2,
        main: 0xffd54a,
        halo: 0xffe68a
      };
    }

    if (layer === 'cosmic') {
      return {
        core: 0xf1d6ff,
        main: 0xc66cff,
        halo: 0xdca4ff
      };
    }

    return {
      core: 0xeaffec,
      main: 0x47ff6a,
      halo: 0xa8ffb0
    };
  }

  private isAwakeningRootNode(node: MapNode): boolean {
    return node.id === 'n-great-awakening';
  }

  private createEarthPlaceholderTexture(renderer: THREE.WebGLRenderer): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const context = canvas.getContext('2d');

    if (!context) {
      return new THREE.CanvasTexture(canvas);
    }

    const ocean = context.createRadialGradient(390, 132, 30, 512, 256, 510);
    ocean.addColorStop(0, '#123a62');
    ocean.addColorStop(0.48, '#06192d');
    ocean.addColorStop(1, '#01050d');
    context.fillStyle = ocean;
    context.fillRect(0, 0, canvas.width, canvas.height);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;
    return texture;
  }


  private createEarthTexture(renderer: THREE.WebGLRenderer): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 2048;
    canvas.height = 1024;
    const context = canvas.getContext('2d');

    if (!context) {
      return new THREE.CanvasTexture(canvas);
    }

    const ocean = context.createRadialGradient(760, 270, 80, 1024, 512, 980);
    ocean.addColorStop(0, '#1b5b8e');
    ocean.addColorStop(0.34, '#0a2b4d');
    ocean.addColorStop(0.72, '#031426');
    ocean.addColorStop(1, '#010610');
    context.fillStyle = ocean;
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.strokeStyle = 'rgba(116, 203, 255, 0.13)';
    context.lineWidth = 1.2;
    for (let x = 0; x <= canvas.width; x += 256) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, canvas.height);
      context.stroke();
    }
    for (let y = 0; y <= canvas.height; y += 128) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(canvas.width, y);
      context.stroke();
    }

    const landPaths = [
      'M135 185 C185 112 292 82 392 110 C462 130 503 176 580 190 C540 235 466 228 430 280 C392 336 316 330 286 388 C242 372 198 340 176 292 C158 252 102 244 135 185 Z',
      'M284 382 C332 380 382 402 420 438 C384 454 336 440 304 418 C284 406 268 396 284 382 Z',
      'M438 444 C494 476 538 548 526 624 C514 704 555 755 528 834 C466 820 420 750 405 674 C388 590 342 542 378 478 C392 452 414 438 438 444 Z',
      'M470 48 C548 20 650 50 690 112 C628 168 530 156 478 96 C462 78 456 62 470 48 Z',
      'M710 205 C770 165 870 170 936 222 C890 252 858 278 812 268 C768 260 732 248 710 205 Z',
      'M790 300 C862 256 940 310 958 402 C978 506 922 612 846 706 C778 632 720 526 736 410 C744 356 750 324 790 300 Z',
      'M908 190 C1030 120 1238 160 1354 260 C1440 334 1444 426 1362 492 C1286 552 1198 490 1114 520 C1034 548 998 490 1030 420 C1068 338 956 298 908 190 Z',
      'M1034 434 C1082 450 1110 502 1088 558 C1052 540 1022 492 1034 434 Z',
      'M1155 516 C1214 512 1264 548 1294 602 C1228 628 1168 594 1155 516 Z',
      'M1240 646 C1334 612 1440 650 1488 730 C1402 800 1270 770 1240 646 Z',
      'M970 628 C1002 664 1008 720 978 760 C948 720 944 668 970 628 Z',
      'M70 826 C276 790 492 828 704 812 C920 796 1112 790 1530 834 C1360 884 344 884 70 826 Z'
    ];

    context.save();
    context.scale(canvas.width / 1600, canvas.height / 900);
    for (const d of landPaths) {
      const path = new Path2D(d);
      context.fillStyle = 'rgba(142, 132, 92, 0.92)';
      context.fill(path);
      context.strokeStyle = 'rgba(140, 215, 255, 0.34)';
      context.lineWidth = 2.2;
      context.stroke(path);
    }

    const cities = [
      [284, 260, 3], [330, 238, 2], [378, 298, 2.5], [466, 418, 2.5],
      [452, 612, 3], [482, 702, 2], [804, 242, 3], [846, 232, 2.5],
      [900, 258, 2], [870, 352, 3.5], [900, 426, 2], [886, 520, 2.5],
      [1058, 292, 3], [1138, 322, 2.5], [1204, 368, 2], [1280, 438, 2.5],
      [1348, 690, 3]
    ];
    for (const [x, y, r] of cities) {
      const glow = context.createRadialGradient(x, y, 0, x, y, r * 5);
      glow.addColorStop(0, 'rgba(255, 220, 124, 0.95)');
      glow.addColorStop(0.36, 'rgba(255, 195, 85, 0.45)');
      glow.addColorStop(1, 'rgba(255, 195, 85, 0)');
      context.fillStyle = glow;
      context.beginPath();
      context.arc(x, y, r * 5, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = 'rgba(255, 228, 154, 0.95)';
      context.beginPath();
      context.arc(x, y, r, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;
    return texture;
  }

  private latLonToVector3(lat: number, lon: number, radius: number): THREE.Vector3 {
    const latRad = (lat * Math.PI) / 180;
    const lonRad = ((lon + 180) * Math.PI) / 180;

    return new THREE.Vector3(
      -radius * Math.cos(latRad) * Math.cos(lonRad),
      radius * Math.sin(latRad),
      radius * Math.cos(latRad) * Math.sin(lonRad)
    );
  }

  private orbitPointForNode(node: MapNode, radius: number): THREE.Vector3 {
    const hash = Array.from(node.id).reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const angle = ((hash * 137.508) % 360) * Math.PI / 180;
    const verticalWave = Math.sin(hash * 0.73);
    const y = verticalWave * radius * 0.34;
    const ringRadius = Math.sqrt(Math.max(0.1, radius * radius - y * y));

    return new THREE.Vector3(
      Math.cos(angle) * ringRadius,
      y,
      Math.sin(angle) * ringRadius
    );
  }

  private globeLayerRadius(layer: VisualLayer): number {
    if (layer === 'physical') return 2.66;
    if (layer === 'conceptual') return 3.46;
    if (layer === 'occult') return 3.72;
    return 4.35;
  }

  private createGlobeArcPoints(source: THREE.Vector3, target: THREE.Vector3): THREE.Vector3[] {
    const start = source.clone().normalize();
    const end = target.clone().normalize();
    const dot = THREE.MathUtils.clamp(start.dot(end), -0.98, 0.98);
    const omega = Math.acos(dot);
    const sinOmega = Math.sin(omega);
    const maxEndpointRadius = Math.max(source.length(), target.length());
    const baseRadius = maxEndpointRadius + 0.16;
    const isOrbitalConnection = maxEndpointRadius > 2.9;
    const lift = Math.min(isOrbitalConnection ? 1.08 : 0.72, Math.max(isOrbitalConnection ? 0.34 : 0.18, omega * 0.2));
    const points: THREE.Vector3[] = [];

    for (let index = 0; index <= 64; index += 1) {
      const t = index / 64;
      let direction: THREE.Vector3;

      if (Math.abs(sinOmega) < 0.001) {
        direction = start.clone().lerp(end, t).normalize();
      } else {
        const a = Math.sin((1 - t) * omega) / sinOmega;
        const b = Math.sin(t * omega) / sinOmega;
        direction = start.clone().multiplyScalar(a).add(end.clone().multiplyScalar(b)).normalize();
      }

      const altitude = Math.sin(Math.PI * t) * lift;
      points.push(direction.multiplyScalar(baseRadius + altitude));
    }

    return points;
  }

  private edgeColor(edge: MapEdge): THREE.Color {
    const layer = this.edgeVisualLayer(edge);
    if (layer === 'physical') return new THREE.Color(0x67ff93);
    if (layer === 'occult') return new THREE.Color(0xffc247);
    if (layer === 'cosmic') return new THREE.Color(0xc66cff);
    return new THREE.Color(0xffd54a);
  }

  private nodeToneColor(node: MapNode): number {
    if (this.nodeVisualLayer(node) === 'physical') {
      return 0x47ff6a;
    }

    const tone = this.nodeTone(node);
    const colors: Record<string, number> = {
      blue: 0x48a6ff,
      green: 0x67ff75,
      gold: 0xffd45a,
      purple: 0xc66cff,
      red: 0xff616e,
      cyan: 0x61f7ff,
      physical: 0x67ff75,
      conceptual: 0xffd54a,
      occult: 0xffc247,
      cosmic: 0xc66cff
    };

    return colors[tone] ?? 0xffd45a;
  }

  private clearGroup(group: THREE.Group): void {
    const disposeObject = (object: THREE.Object3D): void => {
      for (const child of [...object.children]) {
        disposeObject(child);
      }

      const disposable = object as THREE.Mesh | THREE.Line | THREE.Sprite;
      disposable.geometry?.dispose();
      const material = disposable.material;
      if (Array.isArray(material)) {
        material.forEach((item) => {
          (item as THREE.Material & { map?: THREE.Texture }).map?.dispose();
          item.dispose();
        });
      } else {
        (material as THREE.Material & { map?: THREE.Texture } | undefined)?.map?.dispose();
        material?.dispose();
      }
    };

    while (group.children.length) {
      const child = group.children.pop();
      if (child) {
        disposeObject(child);
      }
    }
  }

  private isMainNode(node: MapNode): boolean {
    if (node.categoryId === 'core') {
      return true;
    }

    return node.importance >= 4 || this.nodeDegree(node.id) >= 6;
  }

  private nodeDegree(nodeId: string): number {
    return this.degreeByNode().get(nodeId) ?? 0;
  }

  private isNodeWithinViewport(node: MapNode, box: ViewBox, margin: number): boolean {
    return node.position.x >= box.x - margin
      && node.position.x <= box.x + box.width + margin
      && node.position.y >= box.y - margin
      && node.position.y <= box.y + box.height + margin;
  }

  private projectGlobePoint(node: MapNode): { x: number; y: number; visible: boolean } {
    const point = this.globePointForNode(node);
    const lon = ((point.lon + this.globeRotation()) * Math.PI) / 180;
    const lat = (point.lat * Math.PI) / 180;
    const radius = 680;
    const cosLat = Math.cos(lat);
    const z = cosLat * Math.cos(lon);
    const x = radius * cosLat * Math.sin(lon);
    const y = -radius * Math.sin(lat) * 0.82;
    const depthScale = 0.72 + Math.max(0, z) * 0.28;

    return {
      x: x * depthScale,
      y: y * depthScale,
      visible: z > -0.18
    };
  }

  private globePointForNode(node: MapNode): GlobePoint {
    const canonical = this.canonicalNode(node);
    const text = `${canonical.id} ${canonical.title} ${canonical.titleOriginal ?? ''} ${canonical.summary}`.toLowerCase();
    const knownPoints: Record<string, GlobePoint> = {
      'n-lote1-giza-complex': { lat: 29.98, lon: 31.13 },
      'n-lote1-sphinx': { lat: 29.98, lon: 31.14 },
      'n-pyramids': { lat: 29.98, lon: 31.13 },
      'n-lote1-ancient-egypt': { lat: 26.82, lon: 30.8 },
      'n-lote1-gobekli-tepe': { lat: 37.22, lon: 38.92 },
      'n-lote1-bosnian-pyramid': { lat: 43.98, lon: 18.18 },
      'n-lote1-japanese-underwater-pyramids': { lat: 24.45, lon: 123.01 },
      'n-lote1-stone-circles': { lat: 51.18, lon: -1.83 },
      'n-lote1-megalithic-sites': { lat: 51.18, lon: -1.83 },
      'n-lote1-crop-circles': { lat: 51.35, lon: -1.99 },
      'n-lote1-ley-lines': { lat: 51.18, lon: -1.83 },
      'n-lote1-earth-grid': { lat: 29.98, lon: 31.13 },
      'n-lote1-vortex': { lat: 34.87, lon: -111.76 },
      'n-atlantis-lemuria-mu': { lat: 31, lon: -38 },
      'n-lote1-atlantis': { lat: 31, lon: -38 },
      'n-lote1-lemuria': { lat: -20, lon: 80 },
      'n-antarctica': { lat: -82, lon: 0 },
      'n-neuschwabenland': { lat: -72, lon: 10 },
      'n-operation-highjump': { lat: -75, lon: -170 },
      'n-argentina': { lat: -34.6, lon: -58.38 },
      'n-area-51': { lat: 37.24, lon: -115.81 },
      'n-lote1-dulce-base': { lat: 36.94, lon: -106.99 },
      'n-sandia-dulce': { lat: 35.08, lon: -106.52 },
      'n-dumbs': { lat: 39.5, lon: -98.35 },
      'n-lote1-deep-underground-military-bases': { lat: 39.5, lon: -98.35 },
      'n-lote1-secret-underground-bases': { lat: 39.5, lon: -98.35 },
      'n-lote1-underground-base': { lat: 39.5, lon: -98.35 },
      'n-inner-earth': { lat: 84, lon: 0 },
      'n-lote1-hollow-earth': { lat: 84, lon: 0 },
      'n-lote1-inner-earth-civilization': { lat: 84, lon: 0 },
      'n-lote1-agartha': { lat: 31.7, lon: 88.1 },
      'n-lote1-transparent-ancient-caverns': { lat: 31.7, lon: 88.1 },
      'n-lote1-library-of-porthologos': { lat: 84, lon: 0 },
      'n-lote1-ancient-ruins': { lat: 37.22, lon: 38.92 },
      'n-lote1-crystalline-towers-glass-domes': { lat: -82, lon: 0 },
      'n-lote1-h-buildings': { lat: -72, lon: 10 },
      'n-lote1-stasis-giants': { lat: -82, lon: 0 },
      'n-lote1-pre-adamites-elongated-skull-giants': { lat: -82, lon: 0 },
      'n-pre-adamites': { lat: -82, lon: 0 },
      'n-lote1-52-local-star-cluster-protective-barrier-grid': { lat: 0, lon: 0 },
      'n-lote1-the-outer-energetic-barrier': { lat: 0, lon: 90 },
      'n-earth': { lat: 0, lon: 0 },
      'n-lote1-5d-earth': { lat: 0, lon: 0 },
      'n-schumann-resonance': { lat: 0, lon: -30 },
      'n-lote1-icc-bases': { lat: -76, lon: 20 }
    };

    const knownPoint = knownPoints[canonical.id];
    if (knownPoint) {
      return knownPoint;
    }

    if (text.includes('giza') || text.includes('piramide') || text.includes('egypt') || text.includes('egito')) return { lat: 29.98, lon: 31.13 };
    if (text.includes('nazca')) return { lat: -14.83, lon: -74.94 };
    if (text.includes('machu')) return { lat: -13.16, lon: -72.54 };
    if (text.includes('tibet') || text.includes('tibete') || text.includes('himalaya')) return { lat: 31.7, lon: 88.1 };
    if (text.includes('pascoa') || text.includes('easter island')) return { lat: -27.11, lon: -109.35 };
    if (text.includes('ley') || text.includes('earth grid') || text.includes('grid')) return { lat: 51.18, lon: -1.83 };
    if (text.includes('area 51')) return { lat: 37.24, lon: -115.81 };
    if (text.includes('antart') || text.includes('antarctica')) return { lat: -76, lon: 20 };
    if (text.includes('atlanti')) return { lat: 31, lon: -38 };
    if (text.includes('lemuria')) return { lat: -16, lon: 150 };
    if (text.includes('rome') || text.includes('roma') || text.includes('vatican')) return { lat: 41.9, lon: 12.48 };
    if (text.includes('jerusalem')) return { lat: 31.77, lon: 35.21 };
    if (text.includes('meca') || text.includes('mecca')) return { lat: 21.42, lon: 39.82 };
    if (text.includes('stonehenge')) return { lat: 51.18, lon: -1.83 };
    if (text.includes('brasil') || text.includes('brazil')) return { lat: -15.79, lon: -47.88 };
    if (text.includes('tesla')) return { lat: 40.71, lon: -74 };
    if (text.includes('venus')) return { lat: -34, lon: 18 };

    const hash = Array.from(node.id).reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const layer = this.nodeVisualLayer(node);
    const latBase = layer === 'physical' ? 8 : layer === 'cosmic' ? -18 : 26;

    return {
      lat: Math.max(-58, Math.min(62, latBase + ((hash % 46) - 23))),
      lon: ((hash * 47) % 320) - 160
    };
  }

  private startGlobeRotation(): void {
    let last = performance.now();
    const tick = (now: number) => {
      const delta = now - last;
      last = now;

      if (this.isGlobeOverview() && !this.dragging) {
        this.globeRotation.update((rotation) => rotation + delta * 0.0028);
      }

      this.globeAnimationFrame = requestAnimationFrame(tick);
    };

    this.globeAnimationFrame = requestAnimationFrame(tick);
  }

  /**
   * Transicao espacial inspirada em apresentacoes como o Prezi.
   * O conteudo ja existe no mesmo plano; apenas conduzimos a camera ate ele.
   */
  private setViewBoxAnimated(target: ViewBox): void {
    this.cancelViewAnimation();

    const start = this.viewBox();
    const startedAt = performance.now();
    const duration = 620;

    const animate = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      this.viewBox.set({
        x: start.x + (target.x - start.x) * eased,
        y: start.y + (target.y - start.y) * eased,
        width: start.width + (target.width - start.width) * eased,
        height: start.height + (target.height - start.height) * eased
      });

      if (progress < 1) {
        this.viewAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.viewAnimationFrame = null;
      }
    };

    this.viewAnimationFrame = requestAnimationFrame(animate);
  }

  private cancelViewAnimation(): void {
    if (this.viewAnimationFrame !== null) {
      cancelAnimationFrame(this.viewAnimationFrame);
      this.viewAnimationFrame = null;
    }
  }

  private contextNodeIds(nodeId: string, expanded: boolean): Set<string> {
    const data = this.data();
    const ids = new Set<string>([nodeId]);

    if (!data) {
      return ids;
    }

    const direct = data.edges.filter((edge) => edge.source === nodeId || edge.target === nodeId);
    for (const edge of direct) {
      ids.add(edge.source === nodeId ? edge.target : edge.source);
    }

    if (expanded) {
      const firstRing = new Set(ids);
      for (const edge of data.edges) {
        if (edge.strength < 4) {
          continue;
        }

        if (firstRing.has(edge.source)) {
          ids.add(edge.target);
        }

        if (firstRing.has(edge.target)) {
          ids.add(edge.source);
        }
      }
    }

    return ids;
  }

  private nodeLayer(node: MapNode): string {
    return this.nodeVisualLayer(node);
  }

  private pointerToViewBox(event: PointerEvent, canvas: SVGSVGElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const box = this.viewBox();

    return {
      x: ((event.clientX - rect.left) / rect.width) * box.width + box.x,
      y: ((event.clientY - rect.top) / rect.height) * box.height + box.y
    };
  }

  trackNode(_index: number, node: MapNode): string {
    return node.id;
  }

  trackEdge(_index: number, edge: MapEdge): string {
    return edge.id;
  }

  trackIsland(_index: number, island: CategoryIsland): string {
    return island.id;
  }
}
